import React, { useState, useEffect, useRef } from 'react';
import { authFetch, supabase } from './lib/supabase';

// =============================================================================
// VERSION & CONFIG
// Edit these constants to update the version stamp.
// =============================================================================
const VERSION = 'v0.9.17';
const VERSION_DATE = '28 July 2026';

// =============================================================================
// PALETTE — mapped to traumainformedcontent.com
//
// The three logo colours (coral, sky, sage) are DECORATIVE ONLY. As text on
// white they measure 3.38:1, 2.71:1 and 2.67:1 — all below the WCAG 2.2 AA
// requirement of 4.5:1 for body text. A tool that lists WCAG 2.2 AA as a
// framework it checks against cannot fail it on its own status indicators.
//
// So each has a darkened sibling for any use that carries text or sits behind
// white text. The originals stay for dots, borders and gradient bars, which
// are decorative and exempt.
//
//   attentionText  #B8442A   5.4:1 on white, 4.6:1 on #FCEAE3
//   considerText   #1B6E92   5.7:1 on white, 4.9:1 on #DDF0F9
//   noteText       #356B53   6.2:1 on white, 5.3:1 on #E6F0EA
//   harm           #8F4229   replaces #B85A3D, which measured 3.9:1 on the
//                            error card background and failed
// =============================================================================
const PALETTE = {
  bg:            '#FAFAF6',
  surface:       '#FFFFFF',
  ink:           '#0A3D6E',
  inkDeep:       '#062847',
  muted:         '#5C6B7A',
  faint:         '#A0ADB8',
  rule:          '#E0DDD3',
  panel:         '#F0EBDD',
  panelDeep:     '#E8E0CC',
  primary:       '#0A3D6E',
  primaryFg:     '#FFFFFF',
  attention:     '#E5634A', // logo coral — decorative only
  consider:      '#2BA8DC', // logo sky — decorative only
  note:          '#6FAA94', // logo sage — decorative only
  attentionText: '#B8442A',
  considerText:  '#1B6E92',
  noteText:      '#356B53',
  works:         '#3D7A5F',
  harm:          '#8F4229',
};

// Framework chips display the regulatory frameworks that apply under
// each jurisdiction. Each chip is a link to the framework's definitive
// source so users can verify what the tool is checking against — a
// signal that these are real references, not decorative metadata.
// ISO 22458 is paywalled at the ISO site itself; we link to the BSI
// overview page as the next-best authoritative source.
const JURISDICTIONS = {
  UK: {
    label: 'United Kingdom',
    short: 'UK',
    frameworks: [
      { name: 'FCA Consumer Duty',     url: 'https://www.fca.org.uk/firms/consumer-duty-information-firms' },
      { name: 'Fundraising Regulator', url: 'https://www.fundraisingregulator.org.uk/code' },
      { name: 'ASA CAP code',          url: 'https://www.asa.org.uk/codes-and-rulings/advertising-codes.html' },
      { name: 'ISO 22458',             url: 'https://www.bsigroup.com/en-GB/standards/bs-iso-22458/' },
      { name: 'GDS content standards', url: 'https://www.gov.uk/guidance/content-design/writing-for-gov-uk' },
      { name: 'WCAG 2.2 AA',           url: 'https://www.w3.org/TR/WCAG22/' },
    ],
  },
  EU: {
    label: 'European Union',
    short: 'EU',
    frameworks: [
      { name: 'European Accessibility Act', url: 'https://employment-social-affairs.ec.europa.eu/policies-and-activities/social-protection-social-inclusion/persons-disabilities/union-equality-strategy-rights-persons-disabilities-2021-2030/european-accessibility-act_en' },
      { name: 'EN 301 549',                 url: 'https://www.etsi.org/deliver/etsi_en/301500_301599/301549/' },
      { name: 'ISO 22458',                  url: 'https://www.bsigroup.com/en-GB/standards/bs-iso-22458/' },
    ],
  },
  US: {
    label: 'United States',
    short: 'US',
    frameworks: [
      { name: 'Plain Writing Act', url: 'https://www.plainlanguage.gov/law/' },
      { name: 'Section 508',       url: 'https://www.section508.gov/' },
      { name: 'ADA',               url: 'https://www.ada.gov/' },
      { name: 'ISO 22458',         url: 'https://www.bsigroup.com/en-GB/standards/bs-iso-22458/' },
    ],
  },
};

const ROLE_CHIPS = [
  "I'm drafting this myself",
  "I'm editing what a colleague drafted",
  "I'm publishing content my team wrote",
  "I received this",
  "I'm reviewing third-party work",
];

// =============================================================================
// PROFESSIONAL-TIER DRAFTING CONTEXT
//
// These three controls — target reading age, audience state, content urgency —
// are visible to all users but only functional for Professional and Team tiers.
// Free users see them locked with a Pro badge; clicking a locked control
// reveals a small in-place upsell pointing to the pricing page.
//
// Values map to the validation lists in /api/review.js — keep these in sync
// if either is edited. The backend silently drops Pro context fields for
// non-Pro users, so the front-end gate is a UX courtesy, not a security
// boundary.
// =============================================================================
const AUDIENCE_STATES = [
  { value: 'in-distress',            label: 'In distress' },
  { value: 'in-grief',               label: 'In grief' },
  { value: 'in-pain',                label: 'In pain' },
  { value: 'in-financial-difficulty', label: 'In financial difficulty' },
  { value: 'cognitive-load',         label: 'Under cognitive load' },
  { value: 'not-first-language',     label: 'Not first language' },
  { value: 'accessibility-need',     label: 'Accessibility need' },
  { value: 'time-pressured',         label: 'Time-pressured' },
];

const URGENCY_LEVELS = [
  { value: 'routine',        label: 'Routine' },
  { value: 'time-sensitive', label: 'Time-sensitive' },
  { value: 'crisis',         label: 'Crisis' },
];

// Range for the target-reading-age dropdown. Matches the validation
// bounds in /api/review.js (integer between 5 and 20). Step is 1.
const TARGET_AGE_OPTIONS = Array.from({ length: 14 }, (_, i) => i + 5);

const PRO_PRICING_URL = 'https://traumainformedcontent.com/rembrandt-editor-plus/';

// =============================================================================
// STRIPE UPGRADE LINK
//
// PASTE YOUR STRIPE PAYMENT LINK BETWEEN THE QUOTES BELOW.
//
//   Sandbox testing:  https://buy.stripe.com/test_xxxxxxxxxx
//   Live:             https://buy.stripe.com/xxxxxxxxxx
//
// Leave it empty and the upgrade control falls back to the pricing page. That
// is the correct behaviour until the link exists — better a page explaining
// the plan than a checkout whose payment cannot be attributed to anyone.
//
// buildUpgradeUrl appends the signed-in user's Supabase id as
// client_reference_id. That parameter is the ONLY thing that tells
// /api/stripe-webhook which account paid. Without it the webhook logs
// "matched no profile row" and the customer stays on free.
//
// This is also why the upgrade button lives here rather than on the WordPress
// pricing page: a static page cannot know who is clicking it.
// =============================================================================
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/test_28EeVd1i20Xmb621K83sI00';

const buildUpgradeUrl = (userId, email) => {
  if (!STRIPE_PAYMENT_LINK || !userId) return PRO_PRICING_URL;
  try {
    const url = new URL(STRIPE_PAYMENT_LINK);
    url.searchParams.set('client_reference_id', userId);
    if (email) url.searchParams.set('prefilled_email', email);
    return url.toString();
  } catch (e) {
    // A malformed link should send people somewhere useful rather than
    // nowhere. The pricing page always works.
    console.error('STRIPE_PAYMENT_LINK is not a valid URL:', e);
    return PRO_PRICING_URL;
  }
};

// =============================================================================
// CHARACTER LIMITS — tier-aware
//
// Free is held at a level suitable for occasional review of a single letter
// or page. Professional and Team are raised to take a long letter set or a
// policy passage in one pass without bumping the limit.
//
// The server enforces the matching ceiling in api/review.js
// (MAX_TEXT_LENGTH_BY_TIER). These two MUST move together: if the client
// allowed more than the server, a Pro user pasting past 8,000 would clear
// the client check and then be rejected by the server; if the server allowed
// more than the client, the client would block input the server would happily
// have taken. Keep them in lockstep.
//
// The active limit is derived from the loaded tier at render time (see
// CHAR_LIMIT inside the component). An unrecognised tier falls back to the
// free ceiling, so a malformed tier value can never grant a larger allowance
// than it should.
// =============================================================================
const CHAR_LIMITS = {
  free: 8000,
  professional: 20000,
  team: 20000,
};

const PDF_MAX_BYTES = 2_500_000; // ~2.5 MB raw, ~3.3 MB base64 — sits under Vercel body limit

// Client-side fetch timeout for the /api/review call.
//
// This MUST sit above the server's own budget, not level with it. The
// previous value was 180_000, which was identical to the Vercel
// maxDuration and to the server's two-attempt Anthropic budget — three
// independent limits all expiring at the same instant, so a long review
// died three ways at once and the user got whichever error won the race.
//
// The server now enforces its own deadline (MODEL_TOTAL_BUDGET_MS in
// api/review.js, 480s) and always returns a real HTTP error before the
// platform kills it. This client ceiling is the backstop for a genuinely
// dead connection, so it sits comfortably above both: 600s.
//
// The Vercel function maxDuration is set in vercel.json, NOT in
// api/review.js. The `export const maxDuration` in that file is
// overridden by the vercel.json `functions` block. If you change one,
// change both.
const REVIEW_FETCH_TIMEOUT_MS = 600_000;
const ABOUT_DISMISS_KEY = 'rb_about_dismissed_v3';

const SITE = 'https://traumainformedcontent.com';

// Max length of the optional document title. Matches validateDocumentTitle
// in api/review.js.
const DOC_TITLE_MAX = 120;

// =============================================================================
// NAV LINKS
//
// The full set is the marketing-site navigation. In the header we now split
// it: only a short primary set shows in the desktop nav band (the rest live
// in the footer, which already carries every one of them), while the mobile
// dropdown still lists the complete set so nothing becomes unreachable on a
// small screen. Editing DESKTOP_NAV_LABELS is how you tune what shows up top
// without losing anything from the mobile menu or the footer.
// =============================================================================
const NAV_LINKS = [
  { label: 'What is trauma-informed content?', href: `${SITE}/what-is-trauma-informed-content/` },
  { label: 'Resources',                         href: `${SITE}/resources/` },
  { label: 'Pricing',                           href: `${SITE}/rembrandt-editor-plus/` },
  { label: 'Help',                              href: `${SITE}/help/` },
  { label: 'About',                             href: `${SITE}/about-us/` },
  { label: 'Contact',                           href: `${SITE}/contact-us/` },
];

// Which links appear in the slimmed desktop nav band. Everything else stays
// in the mobile dropdown and the footer. Pricing earns its place as the
// conversion path; Help earns its place as the in-app support route.
const DESKTOP_NAV_LABELS = ['Pricing', 'Help'];
const DESKTOP_NAV_LINKS = NAV_LINKS.filter((l) => DESKTOP_NAV_LABELS.includes(l.label));

const EXAMPLE = `Dear Occupier,

Our records show that you have failed to respond to our previous correspondence dated 15th March 2024 regarding outstanding council tax arrears of £847.32.

You are required to make payment in full within 14 days of the date of this letter. Failure to do so will result in enforcement action being taken against you, which may include the involvement of enforcement agents and additional costs being added to your account.

If you are experiencing financial difficulty, you should contact us immediately.

Yours faithfully,
Revenues Department`;

// =============================================================================
// FEEDBACK CONFIG
// =============================================================================
const FEEDBACK_CATEGORIES = [
  { value: 'bug',        label: 'Bug — something is broken' },
  { value: 'unexpected', label: 'Unexpected output — the review surprised me' },
  { value: 'feature',    label: 'Feature request — something missing' },
  { value: 'general',    label: 'General — anything else' },
];

const FEEDBACK_SEVERITIES = [
  { value: 'blocker',  label: 'Blocker' },
  { value: 'annoying', label: 'Annoying' },
  { value: 'minor',    label: 'Minor' },
];

const FEEDBACK_MESSAGE_MAX = 5000;

// =============================================================================
// ISSUE CATEGORIES — used by the diff view to report movement per category
// =============================================================================
const ISSUE_CATEGORIES = [
  { value: 'cognitive-load',     label: 'Cognitive load' },
  { value: 'emotional-register', label: 'Emotional register' },
  { value: 'trust-grounding',    label: 'Trust and grounding' },
  { value: 'power-agency',       label: 'Power and agency' },
];

// =============================================================================
// READABILITY — deterministic Flesch-Kincaid and SMOG calculations
//
// Why this exists: large language models cannot reliably compute readability
// scores. The same passage can score differently from one run to the next.
// Both formulas are deterministic given words, sentences, syllables, and
// polysyllabic word counts, so they are calculated here and sent to the
// backend, which instructs the model to use the exact decimal values in
// the readingAge and smog fields of its output. For PDFs the source text
// is not available client-side, so the model estimates both itself.
//
// This determinism is also what makes the diff view trustworthy: a reading-age
// delta between two versions is arithmetic, not a second opinion.
//
// v0.9.3 changes:
//   - F-K is now reported to one decimal place. The integer rounding in
//     v0.9.2 produced apparent grade jumps (e.g. 13.49 → 13.51 displayed
//     as 13 → 14) when small edits moved the underlying continuous score
//     across a rounding boundary. The decimal makes proportional changes
//     visible and matches what magnification users like Marian Avery
//     reported needing to see.
//   - SMOG runs alongside F-K. SMOG counts polysyllabic words (3+
//     syllables) over 30 sentences and is the NHS / healthcare standard
//     for patient communications. F-K weights sentence length; SMOG
//     weights vocabulary density. The two together reveal which axis
//     drives the score, which is diagnostically useful — a high F-K with
//     a moderate SMOG means long sentences with simple words; the
//     reverse means short sentences with dense vocabulary.
//
// Tokenizer notes (carried forward from v0.9.2):
//   - countSentences protects common abbreviations (Mr, Mrs, Dr, etc.,
//     e.g., i.e., U.S., U.K. etc.) and decimal numbers (£847.32, 12.5%)
//     before splitting on sentence terminators. Without this protection,
//     "£847.32" would be counted as two sentence terminators, and "e.g."
//     would be counted as a sentence end mid-thought.
//   - countWords, countSyllables, and countPolysyllables work on the
//     original text — they don't see the placeholders.
//
// SMOG reliability:
//   - McLaughlin's original SMOG formula was designed for 30-sentence
//     samples. For shorter texts the formula still works mathematically
//     but the polysyllable count is multiplied by a larger factor, which
//     can produce inflated scores. We display the score regardless and
//     leave interpretation to the user; the calculation breakdown shows
//     them the sentence count.
//
// Formulae:
//   F-K  = 0.39 × (words / sentences) + 11.8 × (syllables / words) − 15.59
//   SMOG = 1.0430 × √(polysyllables × (30 / sentences)) + 3.1291
// =============================================================================

// Zero-width space — used as a placeholder for periods that should NOT be
// treated as sentence terminators (abbreviations, decimal points, ellipses).
const PROTECT = '\u200B';

const ABBREVIATIONS = [
  'Mr', 'Mrs', 'Ms', 'Mx', 'Dr', 'Prof',
  'St', 'Jr', 'Sr', 'No',
  'vs', 'etc', 'cf', 'al',
  'Inc', 'Ltd', 'Co', 'Corp',
  'Rev', 'Hon', 'Capt', 'Lt', 'Sgt', 'Gen', 'Col',
];

const countSentences = (text) => {
  let working = text;

  // Protect decimal numbers (e.g. "847.32", "12.5%") from being split.
  working = working.replace(/(\d)\.(\d)/g, `$1${PROTECT}$2`);

  // Collapse ellipses to a single protected mark so "..." doesn't count
  // as three sentence ends.
  working = working.replace(/\.{2,}/g, PROTECT);

  // Protect dotted abbreviations like "e.g.", "i.e.", "U.S.", "U.K."
  // Pattern: single letter, dot, single letter, dot.
  working = working.replace(/\b([a-zA-Z])\.([a-zA-Z])\./g, `$1${PROTECT}$2${PROTECT}`);

  // Protect the listed word abbreviations followed by a period.
  ABBREVIATIONS.forEach((abbrev) => {
    const re = new RegExp(`\\b${abbrev}\\.`, 'g');
    working = working.replace(re, abbrev + PROTECT);
  });

  // Now split on actual sentence terminators.
  const sentences = working
    .split(/[.!?]+(?:\s|$)/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return Math.max(sentences.length, 1);
};

const countWords = (text) => {
  const words = text.match(/\b[\w'-]+\b/g) || [];
  return words.length;
};

const countSyllablesInWord = (word) => {
  let w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (w.length === 0) return 0;
  if (w.length <= 3) return 1;
  w = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
  w = w.replace(/^y/, '');
  const matches = w.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
};

const countSyllables = (text) => {
  const words = text.match(/\b[\w'-]+\b/g) || [];
  return words.reduce((sum, word) => sum + countSyllablesInWord(word), 0);
};

const countPolysyllables = (text) => {
  const words = text.match(/\b[\w'-]+\b/g) || [];
  return words.reduce((sum, word) => sum + (countSyllablesInWord(word) >= 3 ? 1 : 0), 0);
};

// Round to one decimal place. Used for both F-K and SMOG.
const round1 = (n) => Math.round(n * 10) / 10;

const fleschKincaidGrade = (text) => {
  if (!text || text.trim().length === 0) return null;
  const words = countWords(text);
  const sentences = countSentences(text);
  const syllables = countSyllables(text);
  if (words === 0) return null;
  const grade =
    0.39 * (words / sentences) + 11.8 * (syllables / words) - 15.59;
  return Math.max(1, round1(grade));
};

const smogGrade = (text) => {
  if (!text || text.trim().length === 0) return null;
  const sentences = countSentences(text);
  const polysyllables = countPolysyllables(text);
  if (sentences === 0) return null;
  const grade = 1.0430 * Math.sqrt(polysyllables * (30 / sentences)) + 3.1291;
  return Math.max(1, round1(grade));
};

// Combined calculator that returns both scores and the underlying counts.
// The counts are surfaced in the UI behind a "Show calculation" disclosure
// so the user can audit the maths themselves — this is the diagnostic
// transparency Marian Avery flagged as missing in v0.9.2.
const calculateReadability = (text) => {
  if (!text || text.trim().length === 0) return null;
  const words = countWords(text);
  const sentences = countSentences(text);
  const syllables = countSyllables(text);
  const polysyllables = countPolysyllables(text);
  if (words === 0 || sentences === 0) return null;

  const fk = 0.39 * (words / sentences) + 11.8 * (syllables / words) - 15.59;
  const smog = 1.0430 * Math.sqrt(polysyllables * (30 / sentences)) + 3.1291;

  return {
    readingAge: Math.max(1, round1(fk)),
    smog: Math.max(1, round1(smog)),
    words,
    sentences,
    syllables,
    polysyllables,
    wordsPerSentence: round1(words / sentences),
    syllablesPerWord: Math.round((syllables / words) * 100) / 100,
  };
};

// =============================================================================
// READABILITY CONTEXT — audience-appropriate targets for both F-K and SMOG
//
// Per-metric target descriptors (fkTargetText, smogTargetText) are used
// by the structured two-row readability display. The combined targetText
// remains for the markdown export, where a single-line description reads
// more naturally than a two-row layout.
// =============================================================================
const getReadabilityContext = (readingAge, smog, contentType) => {
  const t = (contentType || '').toLowerCase();

  let fkTarget = null;
  let smogTarget = null;
  let fkTargetText = null;
  let smogTargetText = null;
  let modeName = null;
  let targetText = null;
  let isLivingExperience = false;

  if (t.includes('crisis') || t.includes('emergency')) {
    fkTarget = 7;
    smogTarget = 8;
    fkTargetText = '7 or below';
    smogTargetText = '8 or below';
    modeName = 'crisis or emergency content';
    targetText = 'aim for Flesch-Kincaid 7 or below and SMOG 8 or below';
    isLivingExperience = true;
  } else if (t.includes('service content')) {
    fkTarget = 8;
    smogTarget = 9;
    fkTargetText = 'around 8 — GDS standard';
    smogTargetText = '9 or below — NHS standard';
    modeName = 'service content';
    targetText = 'GDS aims for Flesch-Kincaid around 8, NHS SMOG ≤ 9';
    isLivingExperience = true;
  } else if (t.includes('fundraising') || t.includes('emotional appeal') || t.includes('appeal email') || t.includes('donor')) {
    fkTarget = 11;
    smogTarget = 11;
    fkTargetText = '9 to 11 typical';
    smogTargetText = '10 to 11 typical';
    modeName = 'fundraising content';
    targetText = 'Flesch-Kincaid 9-11 and SMOG 10-11 typical';
  } else if (t.includes('marketing') || t.includes('commercial') || t.includes('promotional')) {
    fkTarget = 10;
    smogTarget = 11;
    fkTargetText = '8 to 10 typical';
    smogTargetText = '9 to 11 typical';
    modeName = 'marketing content';
    targetText = 'Flesch-Kincaid 8-10 and SMOG 9-11 typical';
  } else if (t.includes('organisational') || t.includes('overview') ||
             t.includes('educational') || t.includes('blog') ||
             t.includes('article') || t.includes('explainer')) {
    fkTarget = 12;
    smogTarget = 12;
    fkTargetText = '9 to 12 typical';
    smogTargetText = '10 to 12 typical';
    modeName = 'content for engaged adult audiences';
    targetText = 'Flesch-Kincaid 9-12 and SMOG 10-12 typical';
  }

  if (!fkTarget) return null;

  const fkExceedsTarget = typeof readingAge === 'number' && readingAge > fkTarget;
  const smogExceedsTarget = typeof smog === 'number' && smog > smogTarget;

  return {
    fkTarget,
    smogTarget,
    fkTargetText,
    smogTargetText,
    fkExceedsTarget,
    smogExceedsTarget,
    modeName,
    targetText,
    exceedsTarget: fkExceedsTarget || smogExceedsTarget,
    isLivingExperience,
  };
};

// =============================================================================
// DIFF ENGINE — comparing a revision against the review it came from
//
// This is the feature that turns a one-shot review into a working loop, and
// the one thing here that a reader cannot get by simply running the tool
// twice and squinting.
//
// How issues are matched across versions:
//   An issue is identified by its category plus its excerpt, whitespace- and
//   case-normalised. If the same phrase in the same category appears in both
//   reviews, the problem SURVIVED the rewrite. If it appears only in the
//   earlier one, it was CLEARED. If only in the later one, the rewrite
//   INTRODUCED it.
//
// Why exact excerpt matching rather than fuzzy similarity:
//   Fuzzy matching would let us claim "this is probably the same issue,
//   reworded", and that claim would sometimes be wrong in a way the user
//   could not check. Exact matching is conservative and legible: a phrase
//   either survived verbatim or it did not. An issue that was reworded but
//   not fixed shows up as one cleared and one introduced, which is a slight
//   overstatement of progress in one column and a slight overstatement of
//   regression in the other — visible, symmetrical, and not a false precision
//   dressed up as insight.
//
// The introduced list is the point of the whole exercise. Writers routinely
// fix a cognitive-load problem and lose the reader's route to a human on the
// way, or soften a directive and drop the deadline with it. Nothing else in
// the product catches that, and neither does a human reading the two drafts
// side by side on a Friday afternoon.
// =============================================================================
const issueKey = (issue) => {
  const cat = (issue?.category || 'uncategorised').toLowerCase();
  const excerpt = String(issue?.excerpt || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[""'']/g, '"')
    .trim();
  return `${cat}|${excerpt}`;
};

const countByCategory = (issues) => {
  const counts = {};
  ISSUE_CATEGORIES.forEach((c) => { counts[c.value] = 0; });
  (issues || []).forEach((issue) => {
    const cat = issue?.category;
    if (cat in counts) counts[cat] += 1;
  });
  return counts;
};

const computeDiff = (before, after) => {
  if (!before || !after) return null;

  const beforeIssues = Array.isArray(before.issues) ? before.issues : [];
  const afterIssues = Array.isArray(after.issues) ? after.issues : [];

  const beforeKeys = new Set(beforeIssues.map(issueKey));
  const afterKeys = new Set(afterIssues.map(issueKey));

  const cleared = beforeIssues.filter((i) => !afterKeys.has(issueKey(i)));
  const persisted = afterIssues.filter((i) => beforeKeys.has(issueKey(i)));
  const introduced = afterIssues.filter((i) => !beforeKeys.has(issueKey(i)));

  const beforeCats = countByCategory(beforeIssues);
  const afterCats = countByCategory(afterIssues);

  const beforeFlags = new Set(
    (before.jurisdictionFlags || []).map((f) => String(f?.framework || '').trim())
  );
  const afterFlags = new Set(
    (after.jurisdictionFlags || []).map((f) => String(f?.framework || '').trim())
  );

  const flagsCleared = [...beforeFlags].filter((f) => f && !afterFlags.has(f));
  const flagsRaised = [...afterFlags].filter((f) => f && !beforeFlags.has(f));

  const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

  const fkBefore = num(before.overall?.readingAge);
  const fkAfter = num(after.overall?.readingAge);
  const smogBefore = num(before.overall?.smog);
  const smogAfter = num(after.overall?.smog);

  return {
    fkBefore,
    fkAfter,
    fkDelta: fkBefore != null && fkAfter != null ? round1(fkAfter - fkBefore) : null,
    smogBefore,
    smogAfter,
    smogDelta: smogBefore != null && smogAfter != null ? round1(smogAfter - smogBefore) : null,
    issuesBefore: beforeIssues.length,
    issuesAfter: afterIssues.length,
    cleared,
    persisted,
    introduced,
    categories: ISSUE_CATEGORIES.map((c) => ({
      value: c.value,
      label: c.label,
      before: beforeCats[c.value],
      after: afterCats[c.value],
    })),
    flagsCleared,
    flagsRaised,
    flagsBefore: beforeFlags.size,
    flagsAfter: afterFlags.size,
  };
};

// Format a delta for display. Reading age going DOWN is an improvement, so
// the sign carries meaning the reader has to interpret — we spell it out
// rather than leaving a bare minus sign to be decoded.
const formatDelta = (delta) => {
  if (delta == null) return null;
  if (delta === 0) return 'unchanged';
  const abs = Math.abs(delta);
  return delta < 0 ? `down ${abs}` : `up ${abs}`;
};

const formatReviewDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return '';
  }
};

// =============================================================================
// MARKDOWN BUILDER — used by the Copy review button
// =============================================================================
const buildReviewMarkdown = (results, jurisdiction, diff) => {
  const lines = ['# Rembrandt Editor review', ''];

  if (results.overall) {
    if (results.overall.contentType) lines.push(`**Detected as:** ${results.overall.contentType}`);

    const fk = results.overall.readingAge;
    const sm = results.overall.smog;
    if (fk || sm) {
      const ctx = getReadabilityContext(fk, sm, results.overall.contentType);
      const parts = [];
      if (fk) parts.push(`Flesch-Kincaid ${fk}`);
      if (sm) parts.push(`SMOG ${sm}`);
      const line = `**Reading age:** ${parts.join(' · ')}`;
      if (ctx && ctx.exceedsTarget) {
        lines.push(`${line} — for ${ctx.modeName}, ${ctx.targetText}`);
      } else {
        lines.push(line);
      }
    }

    if (results.overall.contextApplied) lines.push(`**Context applied:** ${results.overall.contextApplied}`);
    if (results.overall.contentType || fk || sm || results.overall.contextApplied) lines.push('');

    if (results.overall.worksStructurally) {
      lines.push('## What works structurally', '', results.overall.worksStructurally, '');
    }

    if (results.overall.summary) {
      lines.push('## Summary', '', results.overall.summary, '');
    }
  }

  // The diff belongs high in the export, because for anyone comparing drafts
  // it is the answer and everything below it is the working.
  if (diff) {
    lines.push('## What changed since the previous version', '');
    if (diff.fkDelta != null) {
      lines.push(`- Flesch-Kincaid ${diff.fkBefore} → ${diff.fkAfter} (${formatDelta(diff.fkDelta)})`);
    }
    if (diff.smogDelta != null) {
      lines.push(`- SMOG ${diff.smogBefore} → ${diff.smogAfter} (${formatDelta(diff.smogDelta)})`);
    }
    lines.push(`- Issues ${diff.issuesBefore} → ${diff.issuesAfter}`);
    lines.push(`- Cleared: ${diff.cleared.length} · Still present: ${diff.persisted.length} · Newly introduced: ${diff.introduced.length}`);
    if (diff.flagsCleared.length > 0) lines.push(`- Flags cleared: ${diff.flagsCleared.join(', ')}`);
    if (diff.flagsRaised.length > 0) lines.push(`- New flags: ${diff.flagsRaised.join(', ')}`);
    lines.push('');

    if (diff.introduced.length > 0) {
      lines.push('### Introduced by this revision', '');
      diff.introduced.forEach((issue) => {
        lines.push(`- **${(issue.category || '').replace(/-/g, ' ')}** — "${issue.excerpt}"`);
      });
      lines.push('');
    }
  }

  if (results.issues?.length > 0) {
    lines.push(`## Specific issues (${results.issues.length})`, '');
    results.issues.forEach((issue) => {
      const sev = issue.severity ? issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1) : '';
      const cat = issue.category ? issue.category.replace(/-/g, ' ') : '';
      lines.push(`### ${sev}${cat ? ' — ' + cat : ''}`, '');
      if (issue.excerpt) lines.push(`> "${issue.excerpt}"`, '');
      lines.push(issue.observation || issue.problem || '', '');
      if (issue.suggestion) lines.push(`**Try instead:** ${issue.suggestion}`, '');
    });
  }

  if (results.jurisdictionFlags?.length > 0) {
    lines.push(`## ${JURISDICTIONS[jurisdiction]?.short || ''} flags`, '');
    lines.push('*Plausible concerns under named frameworks. Not a compliance audit.*', '');
    results.jurisdictionFlags.forEach((flag) => {
      lines.push(`**${flag.framework}**`, '', flag.concern, '');
    });
  }

  if (results.rewrite) {
    lines.push('## Suggested rewrite', '', results.rewrite, '');
  }

  lines.push('---');
  lines.push(`Reviewed with Rembrandt Editor ${VERSION} · ${SITE.replace(/^https?:\/\//, '')}`);

  return lines.join('\n');
};

// =============================================================================
// PROSE FIELD — paragraph-aware renderer for model-emitted long-form text
//
// Why this exists: summary, observation, and suggestion strings can run
// long. Rendering them as a single block of prose buries the structure
// the model already put in the text. The model can emit \n\n where a
// paragraph break would genuinely aid scannability (e.g. in the summary,
// shifting from "what's working" to "what needs attention"); we split
// on those and render each chunk as its own <p>. Single-paragraph
// content renders as a plain <div> so layout doesn't shift around for
// the common case.
//
// We deliberately don't support markdown — bullets, bold, headings —
// because the model's outputs are constrained JSON and adding markdown
// rendering opens a much larger surface area than the problem warrants.
// Paragraph breaks are the smallest useful intervention.
// =============================================================================
const ProseField = ({ text, className }) => {
  if (!text) return null;
  const trimmed = String(text).trim();
  if (!trimmed) return null;
  const paras = trimmed.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (paras.length <= 1) {
    return <div className={className}>{trimmed}</div>;
  }
  return (
    <div className={className}>
      {paras.map((p, i) => (
        <p key={i} className="rb-prose-para">{p}</p>
      ))}
    </div>
  );
};

// =============================================================================
// EMAIL VALIDATION — used by the sign-in modal
//
// Deliberately permissive. The real check is whether Supabase can deliver a
// magic link to the address; this just catches obvious typos before we waste
// a round-trip. We don't try to enforce RFC 5322 — that path leads to a
// regex no one can read and false negatives on valid addresses.
// =============================================================================
const isPlausibleEmail = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (trimmed.length < 5 || trimmed.length > 320) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

// Derive a single-character avatar glyph from an email address. Falls back
// to a neutral dot if there's nothing usable. Used as the compact account
// trigger so the header carries one small control instead of a wide pill.
const avatarInitial = (email) => {
  if (typeof email !== 'string') return '•';
  const trimmed = email.trim();
  if (!trimmed) return '•';
  const first = trimmed[0];
  return /[a-z0-9]/i.test(first) ? first.toUpperCase() : '•';
};

// =============================================================================
// COMPONENT
// =============================================================================
export default function App() {
  const [content, setContent] = useState('');
  const [pdfFile, setPdfFile] = useState(null); // { name, data, size } when set
  const [role, setRole] = useState('');
  const [notes, setNotes] = useState('');
  const [jurisdiction, setJurisdiction] = useState('UK');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [reviewCopied, setReviewCopied] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [aboutDismissed, setAboutDismissed] = useState(false);
  const [reviewPhase, setReviewPhase] = useState(-1);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Feedback modal state
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('');
  const [feedbackSeverity, setFeedbackSeverity] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  // ---------------------------------------------------------------------------
  // Auth + sign-in modal state
  //
  // session / userEmail / userId: populated from the same auth listener that
  // loads the tier (see the effect below). Anonymous users have session ===
  // null. userId is held separately because the Stripe upgrade link needs it
  // — it becomes client_reference_id, which is the only thing that tells the
  // webhook which account paid.
  //
  // signInOpen etc.: the magic-link modal. It mirrors the feedback modal's
  // lifecycle — overlay click to close, ESC to close, focus trap, return
  // focus to trigger. signInSent flips to true once signInWithOtp succeeds
  // so we can show the "check your email" confirmation in place of the form.
  //
  // We do NOT add a second onAuthStateChange listener. The existing tier
  // effect already subscribes; we extend its callback to also set session,
  // email and id. When the magic link is clicked in the user's email client,
  // the resulting redirect loads the app fresh, Supabase restores the
  // session, getSession resolves it, and everything populates. No polling,
  // no extra listener.
  // ---------------------------------------------------------------------------
  const [session, setSession] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userId, setUserId] = useState(null);

  const [signInOpen, setSignInOpen] = useState(false);
  const [signInEmail, setSignInEmail] = useState('');
  const [signInSubmitting, setSignInSubmitting] = useState(false);
  const [signInSent, setSignInSent] = useState(false);
  const [signInError, setSignInError] = useState(null);
  const [signingOut, setSigningOut] = useState(false);

  // ---------------------------------------------------------------------------
  // Account dropdown state
  //
  // accountMenuOpen drives the popover that holds everything the old wide
  // account pill used to spread across the header: the email, the tier badge,
  // the upgrade link (free only), review history (Pro only), Send feedback,
  // and Sign out. One trigger button, one menu.
  //
  // The menu manages its own focus and dismissal — it is a separate
  // interactive surface alongside the modals, so rather than bend the modal
  // focus-trap effect to cover it, it gets small self-contained handlers:
  // outside-click and Escape close it and return focus to the trigger. We
  // deliberately do NOT trap focus inside it; a menu is not a modal, and Tab
  // should be free to move out of it, closing it on the way, which is the
  // conventional menu-button behaviour.
  // ---------------------------------------------------------------------------
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Professional-tier state
  //
  // tier: 'free' | 'professional' | 'team'. Fetched on mount from the
  // signed-in user's profile row. Defaults to 'free' for anonymous users
  // or while loading. Drives whether the drafting-context controls below
  // are active or locked, which character limit applies, and whether review
  // history is available.
  //
  // targetReadingAge / audienceStates / urgency: the three drafting-context
  // fields. Always tracked in state so the UI is consistent whether or not
  // the user is currently Pro; only sent to the API when tier permits.
  //
  // proLockMessage: when a free user clicks a locked control, this flips to
  // true and a small inline message appears pointing to the pricing page.
  // ---------------------------------------------------------------------------
  const [tier, setTier] = useState('free');
  const [targetReadingAge, setTargetReadingAge] = useState(null);
  const [audienceStates, setAudienceStates] = useState([]);
  const [urgency, setUrgency] = useState('routine');
  const [proLockMessage, setProLockMessage] = useState(false);

  // ---------------------------------------------------------------------------
  // Review history and revision state
  //
  // documentTitle:   an optional label the user gives this review, so the
  //                  history list reads as their work rather than as forty
  //                  rows saying "Council tax enforcement letter". Left empty,
  //                  the server derives one from the first line of the source.
  //
  // saveSourceText:  explicit consent to retain the pasted text alongside the
  //                  review. Off by default. Note honestly that issue excerpts
  //                  are stored either way — a review without excerpts is not
  //                  a review — so this controls the full document, not every
  //                  trace of it.
  //
  // revisionOf:      the stored review this draft is a revision of. Set by
  //                  "Review a revision" in the history panel. Sends
  //                  parentReviewId to the API and, when the new review
  //                  arrives, is the left-hand side of the diff.
  //
  // diff:            the computed comparison. Cleared whenever a review is run
  //                  without a parent, so a stale diff can never sit above a
  //                  fresh unrelated review.
  // ---------------------------------------------------------------------------
  const [documentTitle, setDocumentTitle] = useState('');
  const [saveSourceText, setSaveSourceText] = useState(false);
  const [revisionOf, setRevisionOf] = useState(null);
  const [diff, setDiff] = useState(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyBusyId, setHistoryBusyId] = useState(null);

  // Ref to the results section, used to scroll the review into view once it
  // arrives. Without this, users who have scrolled down to set up Pro context
  // fields find the results appearing off-screen (at the top of the
  // right-hand column, above the current scroll position).
  const resultsRef = useRef(null);

  const textareaRef = useRef(null);
  const resultsHeadingRef = useRef(null);
  const pdfInputRef = useRef(null);
  const feedbackFirstFieldRef = useRef(null);
  const feedbackTriggerRef = useRef(null);
  const signInFirstFieldRef = useRef(null);
  const signInTriggerRef = useRef(null);
  const historyTriggerRef = useRef(null);

  // Account dropdown refs: the trigger button (for focus return) and the menu
  // container (for outside-click detection and moving focus inside on open).
  const accountTriggerRef = useRef(null);
  const accountMenuRef = useRef(null);

  // Phase timings (in ms) are scenography of what the server is doing.
  // We can't see the model's actual progress, so these are roughly
  // matched to typical review duration — slow enough that the last
  // phase doesn't tick "current" within 20 seconds of a call that
  // routinely takes 60-90+ seconds to complete. The elapsed counter
  // below is the user's real signal that the request is alive.
  const PHASES = [
    { label: 'Detecting content type and reader stage', ms: 4000 },
    { label: 'Mapping cognitive load and trust points', ms: 12000 },
    { label: `Checking against ${JURISDICTIONS[jurisdiction].short} frameworks`, ms: 10000 },
    { label: 'Drafting suggested rewrite', ms: 14000 },
  ];

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Rethink+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch (e) {} };
  }, []);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage.getItem(ABOUT_DISMISS_KEY) === '1') {
        setAboutDismissed(true);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // ---------------------------------------------------------------------------
  // Load the signed-in user's session, email, id and tier on mount, and keep
  // them fresh if the auth state changes (sign-out in another tab, magic-link
  // sign-in completing, etc.). Anonymous users and lookup failures both
  // resolve to tier 'free' with a null session.
  //
  // This is the single auth listener for the whole app.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function applySession(currentSession) {
      if (cancelled) return;
      setSession(currentSession ?? null);
      setUserEmail(currentSession?.user?.email ?? null);
      setUserId(currentSession?.user?.id ?? null);

      if (!currentSession?.user) {
        setTier('free');
        return;
      }
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('tier')
          .eq('id', currentSession.user.id)
          .single();
        if (cancelled) return;
        if (error || !data) {
          setTier('free');
          return;
        }
        setTier(data.tier || 'free');
      } catch (e) {
        if (!cancelled) setTier('free');
      }
    }

    supabase.auth.getSession().then(({ data }) => applySession(data.session));

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => applySession(newSession)
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Modal lifecycle effects — shared between the feedback, sign-in and
  // history modals.
  //
  // One set of effects handles ESC-to-close, first-field focus and the focus
  // trap for whichever modal is open, routing to the right close handler and
  // container selector so the trap logic isn't duplicated three times.
  // ---------------------------------------------------------------------------
  const anyModalOpen = feedbackOpen || signInOpen || historyOpen;

  // ESC closes the open modal
  useEffect(() => {
    if (!anyModalOpen) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (feedbackOpen) closeFeedback();
      else if (signInOpen) closeSignIn();
      else if (historyOpen) closeHistory();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyModalOpen, feedbackOpen, signInOpen, historyOpen]);

  // Focus first field when feedback modal opens
  useEffect(() => {
    if (feedbackOpen && !feedbackSent) {
      setTimeout(() => feedbackFirstFieldRef.current?.focus(), 50);
    }
  }, [feedbackOpen, feedbackSent]);

  // Focus email field when sign-in modal opens
  useEffect(() => {
    if (signInOpen && !signInSent) {
      setTimeout(() => signInFirstFieldRef.current?.focus(), 50);
    }
  }, [signInOpen, signInSent]);

  // Trap keyboard focus inside whichever modal is open, so Tab and Shift+Tab
  // cycle through fields without escaping to the page behind.
  useEffect(() => {
    if (!anyModalOpen) return;
    const selector = feedbackOpen
      ? '.rb-feedback-modal'
      : signInOpen
        ? '.rb-signin-modal'
        : '.rb-history-modal';
    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const modal = document.querySelector(selector);
      if (!modal) return;
      const focusables = Array.from(modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleTab);
    return () => window.removeEventListener('keydown', handleTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyModalOpen, feedbackOpen, signInOpen, historyOpen]);

  // ---------------------------------------------------------------------------
  // Account dropdown lifecycle
  //
  // Two concerns, kept separate from the modal effects above because a menu
  // is not a modal: it doesn't trap focus, and it dismisses on outside click
  // as well as Escape.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!accountMenuOpen) return;

    const onPointerDown = (e) => {
      const menu = accountMenuRef.current;
      const trigger = accountTriggerRef.current;
      if (menu && menu.contains(e.target)) return;
      if (trigger && trigger.contains(e.target)) return;
      setAccountMenuOpen(false);
    };

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setAccountMenuOpen(false);
      setTimeout(() => accountTriggerRef.current?.focus(), 0);
    };

    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);

    setTimeout(() => {
      const menu = accountMenuRef.current;
      if (!menu) return;
      const firstFocusable = menu.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }, 0);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!loading) {
      setReviewPhase(-1);
      setElapsedSeconds(0);
      return;
    }
    setReviewPhase(0);
    setElapsedSeconds(0);
    let cumulative = 0;
    // Schedule timers for every phase EXCEPT the last one. The last phase
    // stays "current" until the API actually returns, instead of ticking
    // complete on a timer and leaving a confusing dead period where every
    // phase shows ✓ but nothing happens.
    const timers = PHASES.slice(0, -1).map((p, i) => {
      cumulative += p.ms;
      return setTimeout(() => setReviewPhase(i + 1), cumulative);
    });
    const elapsedTimer = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(elapsedTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, jurisdiction]);

  const dismissAbout = () => {
    setAboutDismissed(true);
    try { window.localStorage.setItem(ABOUT_DISMISS_KEY, '1'); } catch (e) {}
  };

  const isPro = tier === 'professional' || tier === 'team';

  // ---------------------------------------------------------------------------
  // Active character limit, derived from the loaded tier.
  //
  // Computed on every render from `tier`. Tier starts as 'free' and updates
  // async once the profile loads, so a Pro user briefly sees the free ceiling
  // for the few hundred milliseconds before their tier resolves, then the
  // higher one. That is harmless: the limit only ever increases on load, never
  // traps them below where they should be. An unrecognised tier falls back to
  // the free ceiling. The server enforces the matching ceiling.
  // ---------------------------------------------------------------------------
  const CHAR_LIMIT = CHAR_LIMITS[tier] ?? CHAR_LIMITS.free;
  const charsLeft = CHAR_LIMIT - content.length;
  const overLimit = charsLeft < 0;

  // --- PDF handling ---
  const handlePdfUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are supported.');
      return;
    }
    if (file.size > PDF_MAX_BYTES) {
      setError(`PDF must be under ${(PDF_MAX_BYTES / 1024 / 1024).toFixed(1)} MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = reader.result;
        const base64 = typeof result === 'string' ? result.split(',')[1] : '';
        setPdfFile({ name: file.name, data: base64, size: file.size });
        setContent('');
        setResults(null);
        setDiff(null);
        setError(null);
        setAnnouncement(`PDF loaded: ${file.name}`);
      } catch (err) {
        setError('Could not read the PDF file.');
      }
    };
    reader.onerror = () => setError('Could not read the PDF file.');
    reader.readAsDataURL(file);

    if (pdfInputRef.current) pdfInputRef.current.value = '';
  };

  const clearPdf = () => {
    setPdfFile(null);
    setError(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const analyse = async () => {
    const hasInput = pdfFile || content.trim();
    if (!hasInput || loading || overLimit) return;

    setLoading(true);
    setError(null);
    setResults(null);
    setDiff(null);
    setAnnouncement('Reviewing content. This can take up to a minute or two for longer passages.');

    // Snapshot notes and the revision parent at the moment the review is run,
    // so later edits can't desync from what was actually sent.
    const notesSnapshot = notes.trim();
    const parentSnapshot = revisionOf;

    // For text input, calculate readability deterministically and send both
    // values to the backend. The model uses these exact decimals in its
    // output so the verdict-meta line and the summary prose stay in sync,
    // and the numbers are consistent across runs — which is also what makes
    // the diff's reading-age delta arithmetic rather than opinion. PDFs fall
    // back to the model's estimates because the source text isn't available
    // client-side.
    const readability = !pdfFile && content ? calculateReadability(content) : null;
    const calculatedReadingAge = readability ? readability.readingAge : null;
    const calculatedSmog = readability ? readability.smog : null;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REVIEW_FETCH_TIMEOUT_MS);

    try {
      const proContext = isPro
        ? {
            ...(targetReadingAge != null && { targetReadingAge }),
            ...(audienceStates.length > 0 && { audienceStates }),
            ...(urgency !== 'routine' && { urgency }),
          }
        : null;
      const includeContext = proContext && Object.keys(proContext).length > 0;

      const shared = {
        jurisdiction,
        role,
        notes: notesSnapshot,
        ...(includeContext && { context: proContext }),
        ...(documentTitle.trim() && { documentTitle: documentTitle.trim() }),
        ...(isPro && saveSourceText && { saveSourceText: true }),
        ...(parentSnapshot?.id && { parentReviewId: parentSnapshot.id }),
      };

      const body = pdfFile
        ? { pdfData: pdfFile.data, pdfFilename: pdfFile.name, ...shared }
        : { content, calculatedReadingAge, calculatedSmog, ...shared };

      const response = await authFetch('/api/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `Request failed (${response.status})`);
      }

      const parsed = await response.json();

      if (notesSnapshot && parsed.overall && !parsed.overall.contextApplied) {
        parsed.overall.contextApplied = notesSnapshot;
      }

      if (readability && parsed.overall) {
        parsed.overall.readabilityBreakdown = {
          words: readability.words,
          sentences: readability.sentences,
          syllables: readability.syllables,
          polysyllables: readability.polysyllables,
          wordsPerSentence: readability.wordsPerSentence,
          syllablesPerWord: readability.syllablesPerWord,
        };
      }

      setResults(parsed);

      // Compute the diff only when this review was explicitly run as a
      // revision of a stored one. A diff against an unrelated document would
      // be noise dressed as insight.
      if (parentSnapshot) {
        setDiff(computeDiff(parentSnapshot, parsed));
      }

      setTimeout(() => {
        if (!resultsRef.current) return;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        resultsRef.current.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start',
        });
      }, 50);

      const issueCount = parsed.issues?.length || 0;
      const flagCount = parsed.jurisdictionFlags?.length || 0;
      setAnnouncement(
        `Review complete. ${issueCount} issue${issueCount === 1 ? '' : 's'} and ${flagCount} ${JURISDICTIONS[jurisdiction].short} flag${flagCount === 1 ? '' : 's'} found.`
      );
    } catch (e) {
      console.error(e);
      // Differentiate the failure shapes so the user gets actionable feedback
      // rather than a generic "something went wrong". The monthly cap now
      // arrives here as a 429 with the server's own message, which already
      // says how many reviews the plan allows and when the period resets, so
      // it needs no special casing — it just surfaces.
      if (e.name === 'AbortError') {
        setError("The review didn't come back in time. Try again, or shorten the passage if it's long. If this keeps happening, please use Send feedback so we can investigate.");
      } else if (e instanceof TypeError) {
        setError("Couldn't reach the review service. Check your connection and try again.");
      } else {
        setError(e.message || 'Something went wrong. Please try again.');
      }
      setAnnouncement('Review failed. Please try again.');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const loadExample = () => {
    setContent(EXAMPLE);
    setPdfFile(null);
    setResults(null);
    setDiff(null);
    setRevisionOf(null);
    setError(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const clear = () => {
    setContent('');
    setResults(null);
    setDiff(null);
    setRevisionOf(null);
    setError(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const toggleChip = (chip) => {
    setRole((current) => (current === chip ? '' : chip));
  };

  // ---------------------------------------------------------------------------
  // Pro-tier field handlers
  //
  // Each handler checks tier before mutating state. Free users get the
  // proLockMessage flag flipped on instead, which surfaces the inline upsell.
  // We don't disable the controls outright because tactile feedback (a click
  // that does something visible) is better than one silently ignored.
  // ---------------------------------------------------------------------------
  const handleProInteraction = (mutator) => {
    if (!isPro) {
      setProLockMessage(true);
      return;
    }
    setProLockMessage(false);
    mutator();
  };

  const toggleAudienceState = (state) => {
    handleProInteraction(() => {
      setAudienceStates((current) =>
        current.includes(state)
          ? current.filter((s) => s !== state)
          : [...current, state]
      );
    });
  };

  const selectUrgency = (level) => {
    handleProInteraction(() => setUrgency(level));
  };

  const selectTargetAge = (value) => {
    handleProInteraction(() => {
      const parsed = value === '' ? null : parseInt(value, 10);
      setTargetReadingAge(Number.isInteger(parsed) ? parsed : null);
    });
  };

  const toggleSaveSourceText = () => {
    handleProInteraction(() => setSaveSourceText((v) => !v));
  };

  const hasContextSet =
    role ||
    notes ||
    documentTitle ||
    targetReadingAge != null ||
    audienceStates.length > 0 ||
    urgency !== 'routine';

  // ---------------------------------------------------------------------------
  // Review history
  //
  // /api/reviews serves the signed-in user's stored reviews under their own
  // token, so Row-Level Security does the ownership check. Free users get a
  // 403 with an honest message rather than an empty list that looks like a
  // bug.
  // ---------------------------------------------------------------------------
  const loadHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await authFetch('/api/reviews', { method: 'GET' });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `Could not load your history (${response.status})`);
      }
      const data = await response.json();
      setHistoryItems(Array.isArray(data.reviews) ? data.reviews : []);
    } catch (e) {
      console.error(e);
      setHistoryError(e.message || 'Could not load your review history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => {
    if (typeof document !== 'undefined') {
      historyTriggerRef.current = document.activeElement;
    }
    setHistoryOpen(true);
    setAccountMenuOpen(false);
    setMobileNavOpen(false);
    loadHistory();
  };

  const closeHistory = () => {
    setHistoryOpen(false);
    setTimeout(() => {
      if (historyTriggerRef.current && typeof historyTriggerRef.current.focus === 'function') {
        historyTriggerRef.current.focus();
      }
    }, 0);
  };

  // Fetch one stored review in full and show it in the results pane. The
  // stored shape is identical to a live review's, so no separate renderer is
  // needed — this is the payoff for normalising the columns rather than
  // storing one opaque blob.
  const openStoredReview = async (id) => {
    setHistoryBusyId(id);
    try {
      const response = await authFetch(`/api/reviews?id=${encodeURIComponent(id)}`, { method: 'GET' });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Could not open that review.');
      }
      const { review, parent } = await response.json();
      setResults(review);
      setDiff(parent ? computeDiff(parent, review) : null);
      setError(null);
      setJurisdiction(review.jurisdiction || jurisdiction);
      closeHistory();
      setAnnouncement('Stored review opened.');
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (e) {
      console.error(e);
      setHistoryError(e.message || 'Could not open that review.');
    } finally {
      setHistoryBusyId(null);
    }
  };

  // Load a stored review's source back into the editor and mark the next
  // review as a revision of it. This is the interaction the diff exists for:
  // without carrying the jurisdiction, role and context forward, the two
  // reviews would not be comparable and the diff would be measuring the
  // settings rather than the writing.
  const startRevision = async (id) => {
    setHistoryBusyId(id);
    try {
      const response = await authFetch(`/api/reviews?id=${encodeURIComponent(id)}`, { method: 'GET' });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Could not open that review.');
      }
      const { review } = await response.json();

      if (review.sourceKind === 'pdf') {
        setHistoryError(
          'That review was run on a PDF, and PDFs are not stored. Paste the revised text and it will still compare against the stored review.'
        );
      }

      if (review.sourceText) {
        setContent(review.sourceText);
        setPdfFile(null);
      } else if (review.sourceKind !== 'pdf') {
        setHistoryError(
          'The text of that review was not retained, so there is nothing to load. Paste the revised version and it will still compare against the stored review.'
        );
      }

      setRevisionOf(review);
      setJurisdiction(review.jurisdiction || jurisdiction);
      if (review.reviewerRole) setRole(review.reviewerRole);
      if (review.notes) setNotes(review.notes);
      if (review.documentTitle) setDocumentTitle(review.documentTitle);
      if (review.context) {
        if (review.context.targetReadingAge != null) setTargetReadingAge(review.context.targetReadingAge);
        if (Array.isArray(review.context.audienceStates)) setAudienceStates(review.context.audienceStates);
        if (review.context.urgency) setUrgency(review.context.urgency);
      }

      setResults(null);
      setDiff(null);
      closeHistory();
      setAnnouncement('Loaded for revision. Edit the text, then run the review to see what changed.');
      setTimeout(() => textareaRef.current?.focus(), 100);
    } catch (e) {
      console.error(e);
      setHistoryError(e.message || 'Could not open that review.');
    } finally {
      setHistoryBusyId(null);
    }
  };

  const deleteStoredReview = async (id) => {
    setHistoryBusyId(id);
    try {
      const response = await authFetch(`/api/reviews?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Could not delete that review.');
      }
      setHistoryItems((items) => items.filter((i) => i.id !== id));
      setAnnouncement('Review deleted.');
    } catch (e) {
      console.error(e);
      setHistoryError(e.message || 'Could not delete that review.');
    } finally {
      setHistoryBusyId(null);
    }
  };

  const cancelRevision = () => {
    setRevisionOf(null);
    setDiff(null);
    setAnnouncement('No longer comparing against a previous review.');
  };

  const copyRewrite = async () => {
    if (!results?.rewrite) return;
    try {
      await navigator.clipboard.writeText(results.rewrite);
      setCopied(true);
      setAnnouncement('Rewrite copied to clipboard.');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.error(e); }
  };

  const copyFullReview = async () => {
    if (!results) return;
    try {
      const md = buildReviewMarkdown(results, jurisdiction, diff);
      await navigator.clipboard.writeText(md);
      setReviewCopied(true);
      setAnnouncement('Full review copied to clipboard as Markdown.');
      setTimeout(() => setReviewCopied(false), 2000);
    } catch (e) { console.error(e); }
  };

  const toggleAccountMenu = () => setAccountMenuOpen((open) => !open);
  const closeAccountMenu = () => setAccountMenuOpen(false);

  // ---------------------------------------------------------------------------
  // Sign-in modal handling
  // ---------------------------------------------------------------------------
  const openSignIn = () => {
    if (typeof document !== 'undefined') {
      signInTriggerRef.current = document.activeElement;
    }
    setSignInOpen(true);
    setMobileNavOpen(false);
    setAccountMenuOpen(false);
    if (!signInEmail && feedbackEmail && isPlausibleEmail(feedbackEmail)) {
      setSignInEmail(feedbackEmail.trim());
    }
  };

  const closeSignIn = () => {
    setSignInOpen(false);
    setTimeout(() => {
      if (signInTriggerRef.current && typeof signInTriggerRef.current.focus === 'function') {
        signInTriggerRef.current.focus();
      }
    }, 0);
    setTimeout(() => {
      setSignInError(null);
      setSignInSent(false);
      setSignInSubmitting(false);
    }, 200);
  };

  const canSubmitSignIn = isPlausibleEmail(signInEmail) && !signInSubmitting;

  const submitSignIn = async () => {
    if (!canSubmitSignIn) return;
    setSignInSubmitting(true);
    setSignInError(null);

    try {
      const redirectTo =
        typeof window !== 'undefined' ? window.location.origin : undefined;
      const { error } = await supabase.auth.signInWithOtp({
        email: signInEmail.trim(),
        options: { emailRedirectTo: redirectTo },
      });
      if (error) throw error;
      setSignInSent(true);
      setAnnouncement('Magic link sent. Check your email to finish signing in.');
    } catch (e) {
      console.error(e);
      const msg = (e && e.message) ? e.message : '';
      if (/rate/i.test(msg) || /too many/i.test(msg)) {
        setSignInError("Too many sign-in requests in a short time. Wait a minute, then try again.");
      } else {
        setSignInError("Couldn't send the magic link. Check the address and try again.");
      }
    } finally {
      setSignInSubmitting(false);
    }
  };

  const resendSignIn = () => {
    setSignInSent(false);
    setSignInError(null);
  };

  const signOut = async () => {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      setAnnouncement('Signed out.');
      setMobileNavOpen(false);
      setAccountMenuOpen(false);
      setHistoryItems([]);
      setRevisionOf(null);
      setDiff(null);
    } catch (e) {
      console.error(e);
    } finally {
      setSigningOut(false);
    }
  };

  // --- Feedback handling ---
  const openFeedback = () => {
    if (typeof document !== 'undefined') {
      feedbackTriggerRef.current = document.activeElement;
    }
    setFeedbackOpen(true);
    setMobileNavOpen(false);
    setAccountMenuOpen(false);
  };

  const closeFeedback = () => {
    setFeedbackOpen(false);
    setTimeout(() => {
      if (feedbackTriggerRef.current && typeof feedbackTriggerRef.current.focus === 'function') {
        feedbackTriggerRef.current.focus();
      }
    }, 0);
    setTimeout(() => {
      setFeedbackCategory('');
      setFeedbackSeverity('');
      setFeedbackMessage('');
      setFeedbackEmail('');
      setFeedbackError(null);
      setFeedbackSent(false);
    }, 200);
  };

  const sendAnother = () => {
    setFeedbackCategory('');
    setFeedbackSeverity('');
    setFeedbackMessage('');
    setFeedbackEmail('');
    setFeedbackError(null);
    setFeedbackSent(false);
  };

  const canSubmitFeedback =
    feedbackCategory && feedbackSeverity && feedbackMessage.trim() && !feedbackSubmitting;

  const submitFeedback = async () => {
    if (!canSubmitFeedback) return;

    setFeedbackSubmitting(true);
    setFeedbackError(null);

    let documentSnapshot = null;
    let documentType = 'none';
    if (content) {
      documentSnapshot = content.slice(0, 500);
      documentType = 'text';
    } else if (pdfFile) {
      documentSnapshot = pdfFile.name;
      documentType = 'pdf';
    }

    try {
      const response = await authFetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: feedbackCategory,
          severity: feedbackSeverity,
          message: feedbackMessage.trim(),
          email: feedbackEmail.trim() || null,
          document_snapshot: documentSnapshot,
          document_type: documentType,
          jurisdiction,
          app_version: VERSION,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Could not send feedback. Please try again.');
      }

      setFeedbackSent(true);
      setAnnouncement('Feedback sent. Thank you.');
    } catch (e) {
      console.error(e);
      setFeedbackError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Severity styling. `dot` is the saturated logo colour, used only for the
  // 8px indicator and the tinted quote background — both decorative and
  // exempt from contrast rules. `text` is the darkened sibling, used wherever
  // the colour carries a word. Keeping these separate is what lets the brand
  // stay recognisable while the text passes 4.5:1.
  const severityMeta = (sev) => {
    if (sev === 'attention') {
      return { dot: PALETTE.attention, text: PALETTE.attentionText, bg: '#FCEAE3', label: 'Attention' };
    }
    if (sev === 'consider') {
      return { dot: PALETTE.consider, text: PALETTE.considerText, bg: '#DDF0F9', label: 'Consider' };
    }
    return { dot: PALETTE.note, text: PALETTE.noteText, bg: '#E6F0EA', label: 'Note' };
  };

  const categoryLabel = (cat) => ({
    'cognitive-load':     'cognitive load',
    'emotional-register': 'emotional register',
    'trust-grounding':    'trust and grounding',
    'power-agency':       'power and agency',
  }[cat] || cat);

  const tierLabel = tier === 'team' ? 'Team' : tier === 'professional' ? 'Pro' : 'Free';

  const upgradeUrl = buildUpgradeUrl(userId, userEmail);

  const css = `
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; }
    button, select, textarea, input { font-family: inherit; }
    button { cursor: pointer; }
    button:disabled { cursor: not-allowed; opacity: 0.5; }
    a { color: inherit; }

    .rb-root {
      --bg: ${PALETTE.bg};
      --surface: ${PALETTE.surface};
      --ink: ${PALETTE.ink};
      --ink-deep: ${PALETTE.inkDeep};
      --muted: ${PALETTE.muted};
      --faint: ${PALETTE.faint};
      --rule: ${PALETTE.rule};
      --panel: ${PALETTE.panel};
      --panel-deep: ${PALETTE.panelDeep};
      --primary: ${PALETTE.primary};
      --primary-fg: ${PALETTE.primaryFg};
      --harm: ${PALETTE.harm};
      /* Decorative logo colours — dots, borders, gradient bars only. */
      --coral: ${PALETTE.attention};
      --sky: ${PALETTE.consider};
      --sage: ${PALETTE.note};
      /* Accessible siblings — anything carrying text, or behind white text. */
      --coral-text: ${PALETTE.attentionText};
      --sky-text: ${PALETTE.considerText};
      --sage-text: ${PALETTE.noteText};
      min-height: 100vh;
      background: var(--bg);
      color: var(--ink);
      font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.5;
    }
    .rb-display { font-family: 'Rethink Sans', -apple-system, BlinkMacSystemFont, sans-serif; font-weight: 600; letter-spacing: -0.015em; }

    .rb-skip {
      position: absolute; top: -40px; left: 16px;
      background: var(--ink); color: var(--bg);
      padding: 8px 14px; border-radius: 4px; z-index: 100;
      font-size: 14px; font-weight: 500;
    }
    .rb-skip:focus { top: 8px; outline: 2px solid var(--primary); outline-offset: 2px; }

    .rb-sr-only {
      position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
      overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;
    }

    /* ---- Header ----
       Not sticky. Sticky headers are an accessibility regression for users
       at high browser magnification (350-500% is common among magnification
       users, well beyond WCAG's 200% test) because the header occupies a
       growing fraction of the viewport and obscures content. position:
       relative is here to act as the positioning context for the mobile
       nav dropdown, which uses top: 100% to sit flush below the header at
       any height. */
    .rb-header {
      border-bottom: 1px solid var(--rule);
      background: var(--bg);
      position: relative; z-index: 20;
    }
    .rb-header-inner { max-width: 1280px; margin: 0 auto; padding: 16px 32px 12px; }
    .rb-header-row { display: flex; align-items: center; justify-content: space-between; gap: 24px; }
    .rb-brand { display: flex; align-items: center; gap: 14px; text-decoration: none; color: var(--ink); }
    .rb-brand:focus-visible { outline: 2px solid var(--primary); outline-offset: 4px; border-radius: 4px; }
    .rb-logo { width: 44px; height: 44px; object-fit: contain; flex-shrink: 0; }
    .rb-brand-text { display: flex; flex-direction: column; line-height: 1.1; }
    .rb-wordmark { font-family: 'Rethink Sans', sans-serif; font-weight: 700; font-size: 26px; letter-spacing: -0.02em; color: var(--ink); }
    .rb-tagline { font-size: 13px; color: var(--muted); margin-top: 4px; letter-spacing: 0.005em; }

    .rb-header-right { display: flex; align-items: center; gap: 10px; }

    .rb-nav { display: flex; align-items: center; gap: 4px; font-size: 14px; }
    .rb-nav a {
      color: var(--muted); text-decoration: none;
      padding: 8px 14px; border-radius: 999px;
      transition: background 0.15s ease, color 0.15s ease;
      background: transparent; border: none; font-family: inherit; font-size: inherit;
    }
    .rb-nav a:hover { background: rgba(10, 61, 110, 0.06); color: var(--ink); }
    .rb-nav a:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    .rb-nav .rb-nav-feedback-btn {
      color: var(--ink) !important; font-weight: 600;
      border: 1px solid var(--ink) !important; border-radius: 999px;
      padding: 8px 14px;
      background: transparent; font-family: inherit; font-size: inherit;
      margin-left: 6px;
    }
    .rb-nav .rb-nav-feedback-btn:hover { background: var(--ink) !important; color: var(--surface) !important; }
    .rb-nav .rb-nav-feedback-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    .rb-nav-toggle {
      display: none;
      background: transparent; border: 1px solid var(--rule);
      color: var(--ink); padding: 8px 14px; border-radius: 999px;
      font-size: 13px; font-weight: 500;
    }
    .rb-nav-toggle:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    /* ---- Account control (dropdown) ---- */
    .rb-account { position: relative; display: flex; align-items: center; }

    .rb-signin-btn {
      background: var(--ink); color: var(--surface);
      border: 1px solid var(--ink);
      padding: 8px 18px; border-radius: 999px;
      font-size: 14px; font-weight: 600;
      transition: background 0.15s ease;
    }
    .rb-signin-btn:hover { background: var(--ink-deep); }
    .rb-signin-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    .rb-account-trigger {
      display: inline-flex; align-items: center; gap: 8px;
      background: var(--surface); border: 1px solid var(--rule);
      border-radius: 999px; padding: 4px 12px 4px 4px;
      font-size: 13px; max-width: 260px;
      transition: border-color 0.15s ease;
    }
    .rb-account-trigger:hover { border-color: var(--ink); }
    .rb-account-trigger:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .rb-account-avatar {
      width: 28px; height: 28px; flex-shrink: 0;
      border-radius: 50%;
      background: var(--ink); color: var(--surface);
      display: inline-flex; align-items: center; justify-content: center;
      font-size: 13px; font-weight: 700;
      font-family: 'Rethink Sans', sans-serif;
    }
    .rb-account-trigger-email {
      color: var(--ink); font-weight: 500;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      max-width: 140px;
    }
    .rb-account-caret {
      color: var(--muted); font-size: 10px; flex-shrink: 0;
      transition: transform 0.15s ease;
    }
    .rb-account-trigger[aria-expanded="true"] .rb-account-caret { transform: rotate(180deg); }

    .rb-account-tier {
      display: inline-block; padding: 2px 8px;
      border-radius: 999px; font-size: 11px; font-weight: 700;
      letter-spacing: 0.04em; text-transform: uppercase;
      background: var(--panel); color: var(--muted);
      flex-shrink: 0;
    }
    .rb-account-tier-pro { background: var(--ink); color: var(--surface); }

    .rb-account-menu {
      position: absolute; top: calc(100% + 8px); right: 0;
      min-width: 240px; max-width: 320px;
      background: var(--surface); border: 1px solid var(--rule);
      border-radius: 12px; padding: 8px;
      box-shadow: 0 16px 40px rgba(10,61,110,0.16);
      z-index: 40;
      animation: rb-menu-in 0.14s ease;
    }
    @keyframes rb-menu-in {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .rb-account-menu-head {
      padding: 8px 10px 10px;
      border-bottom: 1px solid var(--rule);
      margin-bottom: 6px;
    }
    .rb-account-menu-email {
      font-size: 13px; font-weight: 600; color: var(--ink);
      word-break: break-all; line-height: 1.4; margin-bottom: 6px;
    }
    .rb-account-menu-row {
      display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    }
    /* Upgrade link. Was --coral, which measures 3.38:1 on white and fails
       AA for text. --coral-text is the same hue, darkened to 5.4:1. */
    .rb-account-upgrade {
      font-size: 12px; font-weight: 600; color: var(--coral-text);
      text-decoration: underline; text-underline-offset: 3px;
      white-space: nowrap;
    }
    .rb-account-upgrade:hover { color: var(--ink); }
    .rb-account-upgrade:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }

    .rb-account-menu-item {
      display: block; width: 100%; text-align: left;
      background: transparent; border: none;
      padding: 9px 10px; border-radius: 8px;
      font-size: 14px; color: var(--ink); font-family: inherit;
      text-decoration: none;
      transition: background 0.12s ease;
    }
    .rb-account-menu-item:hover { background: rgba(10,61,110,0.06); }
    .rb-account-menu-item:focus-visible { outline: 2px solid var(--primary); outline-offset: -2px; }
    .rb-account-menu-item-danger { color: var(--harm); }
    .rb-account-menu-item-badge {
      display: inline-block; margin-left: 6px;
      padding: 1px 6px; border-radius: 999px;
      background: var(--panel); color: var(--muted);
      font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
      text-transform: uppercase; vertical-align: 1px;
    }

    @media (max-width: 940px) {
      .rb-nav { display: none; }
      .rb-account { display: none; }
      .rb-nav-toggle { display: inline-block; }
      .rb-nav.rb-nav-open {
        display: flex; flex-direction: column; align-items: stretch;
        position: absolute; top: 100%; right: 16px; left: 16px;
        background: var(--surface); border: 1px solid var(--rule);
        padding: 8px; border-radius: 12px;
        box-shadow: 0 16px 40px rgba(10,61,110,0.12); gap: 0;
      }
      .rb-nav.rb-nav-open a, .rb-nav.rb-nav-open .rb-nav-feedback-btn { padding: 12px 16px; border-radius: 6px; text-align: left; }
      .rb-nav-feedback-btn { margin-left: 0 !important; margin-top: 4px; text-align: center !important; }
    }

    /* ---- Account control (mobile, inside Menu dropdown) ---- */
    .rb-account-mobile { display: none; }
    @media (max-width: 940px) {
      .rb-account-mobile {
        display: flex; flex-direction: column; gap: 8px;
        margin-top: 8px; padding-top: 12px;
        border-top: 1px solid var(--rule);
      }
      .rb-account-mobile .rb-signin-btn,
      .rb-account-mobile .rb-signout-btn,
      .rb-account-mobile .rb-history-btn {
        width: 100%; text-align: center;
      }
      .rb-account-mobile-info {
        display: flex; flex-direction: column; gap: 6px;
        padding: 4px 4px 0;
      }
      .rb-account-mobile-email {
        font-size: 14px; font-weight: 500; color: var(--ink);
        word-break: break-all;
      }
      .rb-account-mobile-row {
        display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
      }
      .rb-signout-btn, .rb-history-btn {
        background: transparent; border: 1px solid var(--rule);
        color: var(--muted); padding: 10px 12px; border-radius: 8px;
        font-size: 14px; font-weight: 500;
        transition: border-color 0.15s ease, color 0.15s ease;
      }
      .rb-signout-btn:hover, .rb-history-btn:hover { border-color: var(--ink); color: var(--ink); }
      .rb-signout-btn:focus-visible, .rb-history-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    }

    /* ---- Jurisdiction row ---- */
    .rb-lens-row {
      max-width: 1280px; margin: 0 auto;
      padding: 6px 32px 12px;
      display: flex; flex-wrap: wrap; gap: 12px 22px; align-items: center;
    }
    .rb-jur-group {
      display: inline-flex; gap: 4px; background: var(--surface); padding: 4px;
      border-radius: 999px; border: 1px solid var(--rule);
    }
    .rb-jur-btn {
      padding: 7px 18px; border-radius: 999px; border: none;
      background: transparent; color: var(--muted);
      font-size: 13px; font-weight: 600; letter-spacing: 0.04em;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .rb-jur-btn[aria-pressed="true"] { background: var(--ink); color: var(--surface); }
    .rb-jur-btn:hover:not([aria-pressed="true"]) { background: rgba(10, 61, 110, 0.06); color: var(--ink); }
    .rb-jur-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    /* ---- Frameworks disclosure ---- */
    .rb-fw-details { display: inline-block; }
    .rb-fw-summary {
      cursor: pointer; list-style: none;
      display: inline-flex; align-items: center; gap: 7px;
      font-size: 12px; color: var(--muted);
      padding: 7px 14px; border-radius: 999px;
      border: 1px solid var(--rule); background: var(--surface);
      letter-spacing: 0.01em;
      transition: border-color 0.15s ease, color 0.15s ease;
    }
    .rb-fw-summary::-webkit-details-marker { display: none; }
    .rb-fw-summary::after {
      content: '▾'; font-size: 10px; color: var(--faint);
      transition: transform 0.15s ease;
    }
    .rb-fw-details[open] .rb-fw-summary::after { transform: rotate(180deg); }
    .rb-fw-summary:hover { border-color: var(--ink); color: var(--ink); }
    .rb-fw-summary:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .rb-fw-summary strong { font-weight: 700; color: var(--ink); }

    .rb-fw-list { display: flex; flex-wrap: wrap; gap: 6px; list-style: none; padding: 0; margin: 10px 0 0; align-items: center; }
    .rb-fw {
      font-size: 12px;
      border-radius: 999px;
      background: var(--surface); border: 1px solid var(--rule);
      white-space: nowrap;
      padding: 0;
      overflow: hidden;
    }
    .rb-fw[data-tone="coral"] { border-left: 3px solid var(--coral); }
    .rb-fw[data-tone="sky"]   { border-left: 3px solid var(--sky); }
    .rb-fw[data-tone="sage"]  { border-left: 3px solid var(--sage); }
    .rb-fw-link {
      display: block;
      padding: 4px 10px;
      color: var(--ink);
      text-decoration: none;
      transition: background-color 0.12s ease;
    }
    .rb-fw-link:hover { background: var(--panel); text-decoration: underline; }
    .rb-fw-link:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 1px;
      background: var(--panel);
    }

    @media (max-width: 700px) {
      .rb-header-inner { padding: 14px 20px 10px; }
      .rb-lens-row { padding: 4px 20px 12px; }
      .rb-wordmark { font-size: 22px; }
      .rb-tagline { font-size: 12px; }
      .rb-logo { width: 38px; height: 38px; }
    }

    /* ---- About / intro section ---- */
    .rb-about { max-width: 1280px; margin: 18px auto 0; padding: 0 32px; }
    .rb-about-inner {
      background: var(--panel); border-radius: 12px;
      padding: 20px 24px; position: relative; overflow: hidden;
    }
    .rb-about-inner::before {
      content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
      background: linear-gradient(90deg, var(--coral) 0 33%, var(--sky) 33% 66%, var(--sage) 66% 100%);
    }
    .rb-about-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 22px; }
    @media (max-width: 760px) { .rb-about-grid { grid-template-columns: 1fr; gap: 14px; } }
    .rb-about-block h2 {
      font-family: 'Rethink Sans', sans-serif;
      font-size: 15px; font-weight: 600;
      letter-spacing: -0.005em;
      color: var(--ink); margin: 0 0 8px;
    }
    .rb-about-body { font-size: 16px; line-height: 1.5; color: var(--ink); margin: 0; }
    .rb-about-body + .rb-about-body { margin-top: 10px; }
    .rb-about-body strong { font-weight: 600; }
    .rb-about-meta {
      margin-top: 14px; padding-top: 12px;
      border-top: 1px solid var(--panel-deep);
      font-size: 13px; color: var(--muted); line-height: 1.55;
    }
    .rb-about-meta strong { color: var(--ink); font-weight: 600; }
    .rb-about-meta-link {
      color: var(--ink); text-decoration: underline; text-underline-offset: 3px;
      font-weight: 600; white-space: nowrap;
    }
    .rb-about-close {
      position: absolute; top: 12px; right: 12px;
      background: transparent; border: 1px solid var(--panel-deep);
      color: var(--muted); padding: 4px 12px; border-radius: 999px;
      font-size: 12px; font-weight: 500;
    }
    .rb-about-close:hover { color: var(--ink); border-color: var(--ink); background: rgba(10,61,110,0.04); }
    .rb-about-close:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .rb-about-show {
      display: inline-block; margin: 8px 0 0;
      background: transparent; border: none; padding: 0;
      color: var(--muted); font-size: 13px;
      text-decoration: underline; text-underline-offset: 3px;
    }
    .rb-about-show:hover { color: var(--ink); }
    .rb-about-show:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }

    .rb-help-link {
      display: inline-block; margin: 14px 0 0;
      color: var(--muted); font-size: 13px;
      text-decoration: underline; text-underline-offset: 3px;
    }
    .rb-help-link:hover { color: var(--ink); }
    .rb-help-link:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }

    /* ---- Main grid ---- */
    .rb-main {
      max-width: 1280px; margin: 0 auto;
      padding: 22px 32px 40px;
      display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 32px;
    }
    @media (max-width: 900px) {
      .rb-main { grid-template-columns: 1fr; gap: 24px; padding: 20px 20px 32px; }
      .rb-about { padding: 0 20px; }
      .rb-about-inner { padding: 18px 18px; }
    }

    .rb-section-title { font-family: 'Rethink Sans', sans-serif; font-size: 22px; font-weight: 600; margin: 0 0 14px; letter-spacing: -0.01em; }

    /* ---- Revision banner ----
       Shown above the textarea when the next review will be compared against
       a stored one. Deliberately prominent: a user who has forgotten they are
       in revision mode would be confused by a diff appearing, and a user who
       wants a fresh review needs an obvious way out. */
    .rb-revision-banner {
      display: flex; align-items: flex-start; gap: 12px;
      padding: 12px 14px; margin-bottom: 14px;
      background: var(--panel); border-radius: 8px;
      border-left: 3px solid var(--ink);
      font-size: 13px; line-height: 1.5; color: var(--ink);
    }
    .rb-revision-banner-body { flex: 1 1 auto; }
    .rb-revision-banner-label {
      font-weight: 600; display: block; margin-bottom: 2px;
    }
    .rb-revision-banner-title { font-style: italic; }
    .rb-revision-cancel {
      flex: 0 0 auto;
      background: transparent; border: 1px solid var(--panel-deep);
      color: var(--muted); padding: 4px 12px; border-radius: 999px;
      font-size: 12px; font-weight: 500;
    }
    .rb-revision-cancel:hover { color: var(--ink); border-color: var(--ink); }
    .rb-revision-cancel:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    /* ---- Role and notes inputs ---- */
    .rb-role { margin-bottom: 14px; }
    .rb-notes { margin-bottom: 14px; }
    .rb-field-label {
      display: block; font-size: 12px; color: var(--muted);
      margin-bottom: 6px; font-style: italic;
    }

    .rb-context-details { margin-bottom: 14px; }
    .rb-context-details summary {
      cursor: pointer;
      font-size: 13px;
      color: var(--muted);
      padding: 6px 0;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      list-style: none;
      user-select: none;
    }
    .rb-context-details summary::-webkit-details-marker { display: none; }
    .rb-context-details summary::before {
      content: '▸';
      transition: transform 0.15s ease;
      font-size: 11px;
      color: var(--faint);
    }
    .rb-context-details[open] summary::before { transform: rotate(90deg); }
    .rb-context-details summary:hover { color: var(--ink); }
    .rb-context-details summary:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 2px;
      border-radius: 2px;
    }
    .rb-context-details[open] > .rb-title-field { margin-top: 10px; }
    .rb-notes-input, .rb-title-input {
      width: 100%; padding: 10px 14px;
      border: 1px solid var(--rule); border-radius: 8px;
      background: var(--surface);
      font-size: 14px; line-height: 1.5; color: var(--ink); outline: none;
      font-family: inherit;
      resize: vertical;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .rb-notes-input::placeholder, .rb-title-input::placeholder { color: var(--faint); }
    .rb-notes-input:focus, .rb-title-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(10, 61, 110, 0.15); }
    .rb-title-field { margin-bottom: 14px; }

    .rb-chips { display: flex; flex-wrap: wrap; gap: 6px; }
    .rb-chip {
      background: var(--surface); border: 1px solid var(--rule);
      color: var(--ink); padding: 6px 14px;
      border-radius: 999px; font-size: 12px; font-weight: 500;
      transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    }
    .rb-chip:hover:not([aria-pressed="true"]) { border-color: var(--primary); background: rgba(10, 61, 110, 0.04); }
    .rb-chip[aria-pressed="true"] { background: var(--ink); border-color: var(--ink); color: var(--surface); }
    .rb-chip:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    /* ---- Professional-tier drafting context section ---- */
    .rb-pro-section {
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px dashed var(--rule);
    }
    .rb-pro-header {
      display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
    }
    .rb-pro-heading {
      font-family: 'Rethink Sans', sans-serif;
      font-size: 13px; font-weight: 600;
      color: var(--ink); letter-spacing: -0.005em;
    }
    .rb-pro-badge {
      display: inline-block; padding: 2px 8px;
      background: var(--panel); color: var(--muted);
      border-radius: 999px;
      font-size: 10px; font-weight: 700;
      letter-spacing: 0.06em; text-transform: uppercase;
    }
    .rb-pro-badge-active { background: var(--ink); color: var(--surface); }
    .rb-pro-row {
      display: flex; gap: 20px; align-items: flex-end;
      flex-wrap: wrap; margin-bottom: 10px;
    }
    .rb-pro-row > .rb-pro-field { margin-bottom: 0; flex: 1 1 auto; }
    .rb-pro-row > .rb-pro-field-narrow { flex: 0 0 auto; }
    .rb-pro-field { margin-bottom: 10px; }
    .rb-pro-field:last-child { margin-bottom: 0; }
    .rb-pro-select {
      width: auto; min-width: 180px;
      padding: 7px 12px;
      border: 1px solid var(--rule); border-radius: 8px;
      background: var(--surface);
      font-size: 13px; color: var(--ink);
      font-family: inherit; outline: none; cursor: pointer;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .rb-pro-select:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(10, 61, 110, 0.15); }
    @media (max-width: 600px) {
      .rb-pro-row { gap: 12px; }
      .rb-pro-row > .rb-pro-field { flex-basis: 100%; }
    }

    .rb-pro-locked { opacity: 0.55; cursor: help; }
    .rb-pro-locked:hover { opacity: 0.7; }
    .rb-pro-locked:focus-visible { outline: 2px dashed var(--muted); outline-offset: 2px; }

    /* Retention consent. A checkbox rather than a chip because it is a
       consent decision, not a preference, and checkboxes are what people
       recognise as consent. */
    .rb-retain {
      display: flex; align-items: flex-start; gap: 10px;
      margin-top: 12px; padding-top: 10px;
      border-top: 1px dashed var(--rule);
      font-size: 13px; line-height: 1.5; color: var(--ink);
    }
    .rb-retain input[type="checkbox"] {
      margin: 3px 0 0; flex: 0 0 auto;
      width: 16px; height: 16px; accent-color: ${PALETTE.ink};
    }
    .rb-retain-label { flex: 1 1 auto; cursor: pointer; }
    .rb-retain-note {
      display: block; margin-top: 3px;
      font-size: 12px; color: var(--muted); line-height: 1.5;
    }

    .rb-pro-upsell {
      margin-top: 10px; padding: 10px 12px;
      background: var(--panel);
      border-left: 3px solid var(--coral);
      border-radius: 6px;
      font-size: 13px; line-height: 1.5; color: var(--ink);
    }
    .rb-pro-upsell-link {
      display: inline-block; margin-top: 2px;
      color: var(--ink); font-weight: 600;
      text-decoration: underline; text-underline-offset: 3px;
    }
    .rb-pro-upsell-link:hover { color: var(--ink-deep); }
    .rb-pro-upsell-link:focus-visible {
      outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px;
    }

    /* ---- Textarea ---- */
    .rb-textarea {
      width: 100%; min-height: 220px; padding: 18px 20px;
      border: 1px solid var(--rule); border-radius: 8px;
      background: var(--surface);
      font-size: 15px; line-height: 1.65; color: var(--ink);
      resize: vertical; outline: none;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .rb-textarea::placeholder { color: var(--faint); }
    .rb-textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(10, 61, 110, 0.15); }
    .rb-textarea[aria-invalid="true"] { border-color: var(--coral-text); }

    /* ---- PDF card ---- */
    .rb-pdf-card {
      width: 100%; min-height: 220px;
      border: 1px solid var(--rule); border-radius: 8px;
      background: var(--surface);
      padding: 24px;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 14px; text-align: center;
    }
    .rb-pdf-icon {
      width: 56px; height: 56px;
      background: var(--panel); border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      font-size: 24px; color: var(--ink);
      border-left: 3px solid var(--coral);
    }
    .rb-pdf-name { font-family: 'Rethink Sans', sans-serif; font-weight: 600; font-size: 16px; color: var(--ink); word-break: break-all; max-width: 100%; }
    .rb-pdf-meta { font-size: 12px; color: var(--muted); }
    .rb-pdf-meta-tag { display: inline-block; padding: 2px 8px; background: var(--panel); border-radius: 999px; font-size: 11px; color: var(--ink); margin-left: 6px; font-weight: 600; letter-spacing: 0.04em; }
    .rb-pdf-remove {
      background: transparent; border: 1px solid var(--rule); color: var(--muted);
      padding: 6px 14px; border-radius: 999px; font-size: 12px; font-weight: 500;
      transition: color 0.15s ease, border-color 0.15s ease;
    }
    .rb-pdf-remove:hover { color: var(--ink); border-color: var(--ink); }
    .rb-pdf-remove:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    .rb-meta-row {
      display: flex; justify-content: space-between; align-items: center;
      margin-top: 8px; font-size: 12px; color: var(--muted);
      flex-wrap: wrap; gap: 8px;
    }
    /* Over-limit warning. Was --coral at 3.38:1 — the tool's own
       "you have exceeded a threshold" indicator failing the threshold. */
    .rb-meta-row .rb-over { color: var(--coral-text); font-weight: 600; }
    .rb-link-btn {
      background: none; border: none; padding: 0;
      color: var(--muted); font-size: 12px;
      text-decoration: underline; text-underline-offset: 3px;
    }
    .rb-link-btn:hover { color: var(--ink); }
    .rb-link-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }

    .rb-pdf-toggle {
      margin-top: 10px; font-size: 13px; color: var(--muted);
      display: flex; gap: 6px; align-items: baseline;
    }
    .rb-pdf-toggle .rb-link-btn { font-size: 13px; }
    .rb-pdf-toggle-tag {
      display: inline-block; padding: 1px 8px; background: var(--panel);
      border-radius: 999px; font-size: 11px; color: var(--ink);
      font-weight: 600; letter-spacing: 0;
      margin-left: 4px;
    }

    /* ---- Review action ---- */
    .rb-actions { margin-top: 14px; }
    .rb-primary-btn {
      width: 100%;
      padding: 16px 20px; border: none; border-radius: 8px;
      background: var(--ink); color: var(--surface);
      font-size: 15px; font-weight: 600; letter-spacing: 0.005em;
      box-shadow: 0 6px 20px rgba(10, 61, 110, 0.18);
      transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
    }
    .rb-primary-btn:hover:not(:disabled) { background: var(--ink-deep); }
    .rb-primary-btn:active:not(:disabled) { transform: translateY(1px); }
    .rb-primary-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    /* ---- Review states ---- */
    .rb-empty, .rb-loading, .rb-error-card {
      background: var(--surface); border: 1px solid var(--rule); border-radius: 12px;
      padding: 36px 28px; min-height: 320px;
      display: flex; align-items: center; justify-content: center;
      position: relative; overflow: hidden;
    }
    .rb-empty {
      background:
        radial-gradient(circle at 90% -10%, rgba(43,168,220,0.08), transparent 50%),
        radial-gradient(circle at -10% 110%, rgba(229,99,74,0.07), transparent 50%),
        var(--surface);
    }
    .rb-empty-inner { max-width: 420px; text-align: center; }
    .rb-empty-divider {
      height: 1px; background: var(--rule);
      width: 60px; margin: 0 auto 24px;
    }
    .rb-empty-quote {
      font-family: 'Rethink Sans', sans-serif;
      font-size: 16px; font-weight: 500; font-style: italic;
      color: var(--ink); margin: 8px 0 14px;
      line-height: 1.45; letter-spacing: -0.005em;
    }
    .rb-empty-body { font-size: 13px; color: var(--muted); line-height: 1.6; margin: 0 0 14px; }
    .rb-empty-body:last-child { margin-bottom: 0; }

    /* ---- Phased loading list ---- */
    .rb-loading-inner { max-width: 360px; }
    .rb-loading-title {
      font-family: 'Rethink Sans', sans-serif;
      font-size: 15px; font-style: italic; margin-bottom: 18px;
      text-align: center; color: var(--ink); line-height: 1.4;
    }
    .rb-phases { list-style: none; padding: 0; margin: 0; font-size: 13px; line-height: 1.8; text-align: left; }
    .rb-phase { display: flex; align-items: center; gap: 10px; transition: opacity 0.3s ease, color 0.3s ease, font-weight 0.3s ease; }
    .rb-phase-marker { width: 14px; display: inline-block; text-align: center; font-variant-numeric: tabular-nums; }
    .rb-phase-done    { color: var(--muted); opacity: 1; }
    .rb-phase-current { color: var(--ink);   opacity: 1; font-weight: 600; }
    .rb-phase-pending { color: var(--faint); opacity: 0.55; }

    .rb-loading-elapsed {
      margin-top: 16px; padding-top: 12px;
      border-top: 1px solid var(--rule);
      font-size: 12px; color: var(--faint);
      text-align: center; font-variant-numeric: tabular-nums;
    }

    .rb-error-card {
      background: #FCEAE3; border-color: var(--coral-text);
      color: var(--harm); padding: 18px 22px;
      min-height: auto; font-size: 14px; line-height: 1.55;
      align-items: flex-start; justify-content: flex-start;
    }

    /* ---- Review results ---- */
    .rb-results { display: flex; flex-direction: column; gap: 22px; }
    .rb-results-actions {
      display: flex; justify-content: flex-end; align-items: center;
      gap: 8px; margin-top: -6px; margin-bottom: -6px;
    }
    .rb-results-copy {
      background: var(--ink); border: 1px solid var(--ink); color: var(--surface);
      padding: 7px 16px; border-radius: 999px; font-size: 12px; font-weight: 600;
      letter-spacing: 0.02em;
      transition: background 0.15s ease;
    }
    .rb-results-copy:hover { background: var(--ink-deep); }
    .rb-results-copy:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    .rb-results-nav {
      display: flex; flex-wrap: wrap; gap: 4px;
      padding: 8px 0; background: var(--bg);
      border-bottom: 1px solid var(--rule);
      margin-bottom: 4px; font-size: 13px;
    }
    .rb-results-nav a {
      color: var(--muted); text-decoration: none;
      padding: 5px 12px; border-radius: 999px;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .rb-results-nav a:hover { background: rgba(10, 61, 110, 0.06); color: var(--ink); }
    .rb-results-nav a:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .rb-results-nav .rb-nav-count { display: inline-block; margin-left: 4px; opacity: 0.6; font-variant-numeric: tabular-nums; }

    .rb-anchor { scroll-margin-top: 1rem; }

    /* ---- Diff panel ----
       Sits above the verdict when this review is a revision of a stored one.
       Placed first because for anyone comparing drafts it is the answer, and
       everything below it is the working.

       Direction is spelled out in words rather than left to a minus sign,
       because "reading age down 2.4" and "reading age -2.4" require different
       amounts of interpretation, and the second one is a small cognitive
       tax charged to a reader who came here to reduce cognitive tax. */
    .rb-diff {
      border: 1px solid var(--rule); border-radius: 12px;
      background: var(--surface); overflow: hidden;
    }
    .rb-diff-head {
      padding: 14px 20px;
      border-bottom: 1px solid var(--rule);
      background: var(--panel);
    }
    .rb-diff-title {
      font-family: 'Rethink Sans', sans-serif;
      font-size: 15px; font-weight: 600; color: var(--ink);
      margin: 0;
    }
    .rb-diff-sub {
      font-size: 12px; color: var(--muted); margin-top: 3px; line-height: 1.5;
    }
    .rb-diff-body { padding: 16px 20px; }
    .rb-diff-metrics {
      display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 12px; margin-bottom: 16px;
    }
    .rb-diff-metric {
      padding: 10px 12px; background: var(--panel); border-radius: 8px;
    }
    .rb-diff-metric-label {
      font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
      text-transform: uppercase; color: var(--muted); margin-bottom: 4px;
    }
    .rb-diff-metric-value {
      font-size: 16px; font-weight: 600; color: var(--ink);
      font-variant-numeric: tabular-nums;
    }
    .rb-diff-metric-change { font-size: 12px; margin-top: 2px; font-weight: 600; }
    .rb-diff-better { color: var(--sage-text); }
    .rb-diff-worse  { color: var(--coral-text); }
    .rb-diff-same   { color: var(--muted); font-weight: 500; }

    .rb-diff-group { margin-bottom: 14px; }
    .rb-diff-group:last-child { margin-bottom: 0; }
    .rb-diff-group-label {
      font-size: 13px; font-weight: 600; color: var(--ink);
      margin-bottom: 6px;
      display: flex; align-items: center; gap: 8px;
    }
    .rb-diff-count {
      display: inline-block; min-width: 22px; text-align: center;
      padding: 1px 7px; border-radius: 999px;
      font-size: 12px; font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
    .rb-diff-count-cleared    { background: #E6F0EA; color: var(--sage-text); }
    .rb-diff-count-persisted  { background: var(--panel); color: var(--muted); }
    .rb-diff-count-introduced { background: #FCEAE3; color: var(--coral-text); }

    .rb-diff-list { list-style: none; margin: 0; padding: 0; }
    .rb-diff-list li {
      font-size: 13px; line-height: 1.55; color: var(--ink);
      padding: 6px 0 6px 12px;
      border-left: 2px solid var(--rule);
      margin-bottom: 4px;
    }
    .rb-diff-list li:last-child { margin-bottom: 0; }
    .rb-diff-list-introduced li { border-left-color: var(--coral); }
    .rb-diff-list-cleared li { border-left-color: var(--sage); }
    .rb-diff-excerpt { font-style: italic; color: var(--muted); }
    .rb-diff-cat {
      font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
      color: var(--muted); font-weight: 700; display: block; margin-bottom: 2px;
    }
    .rb-diff-note {
      font-size: 12px; color: var(--muted); line-height: 1.55;
      margin-top: 12px; padding-top: 10px;
      border-top: 1px solid var(--rule); font-style: italic;
    }
    .rb-diff-cats {
      display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px;
    }
    .rb-diff-cat-pill {
      font-size: 12px; padding: 4px 10px;
      border-radius: 999px; background: var(--panel);
      color: var(--ink); font-variant-numeric: tabular-nums;
    }

    .rb-verdict {
      padding: 22px 24px; border-radius: 4px 12px 12px 4px;
      border-left: 4px solid var(--ink);
      background: var(--surface);
      box-shadow: 0 1px 0 var(--rule);
    }
    .rb-verdict-detected { font-size: 13px; font-style: italic; color: var(--muted); margin-bottom: 12px; }
    .rb-verdict-detected strong { font-weight: 600; font-style: normal; color: var(--ink); }
    .rb-verdict-context {
      font-size: 13px; color: var(--ink);
      margin-bottom: 14px; padding: 10px 14px;
      background: var(--panel); border-radius: 6px;
      border-left: 3px solid var(--ink);
      line-height: 1.5;
    }
    .rb-verdict-context strong {
      font-weight: 600; color: var(--ink);
      display: block; margin-bottom: 4px;
      font-size: 13px; letter-spacing: 0;
    }
    .rb-verdict-works {
      border-left: 3px solid var(--ink);
      padding: 2px 0 2px 14px;
      margin: 0 0 16px;
    }
    .rb-verdict-works-label {
      font-family: 'Rethink Sans', sans-serif;
      font-size: 12px; font-weight: 600;
      letter-spacing: 0.06em; text-transform: uppercase;
      color: var(--muted); margin-bottom: 6px;
    }
    .rb-verdict-works-body { font-size: 16px; line-height: 1.65; color: var(--ink); }
    .rb-verdict-summary { font-size: 16px; line-height: 1.65; color: var(--ink); }
    .rb-verdict-meta {
      display: flex; flex-direction: column; gap: 8px;
      font-size: 14px; color: var(--ink); margin-top: 16px;
    }
    .rb-verdict-meta strong { font-weight: 600; color: var(--ink); }
    .rb-verdict-meta-note {
      font-size: 12px; font-style: italic; color: var(--muted); margin-top: 2px;
    }

    /* ---- Structured reading-age display ---- */
    .rb-readability-heading {
      font-size: 13px; font-weight: 600; color: var(--ink); margin-bottom: 2px;
    }
    .rb-readability-rows {
      display: grid;
      grid-template-columns: max-content auto 1fr;
      column-gap: 16px; row-gap: 4px;
      padding: 10px 14px;
      background: var(--panel); border-radius: 6px;
    }
    .rb-readability-row { display: contents; }
    .rb-readability-name { font-size: 14px; color: var(--ink); font-weight: 500; }
    .rb-readability-score {
      font-size: 14px; color: var(--ink); font-weight: 600;
      font-variant-numeric: tabular-nums; text-align: right;
    }
    /* Over-target score. Was --coral on --panel, which measured worse than
       on white. --coral-text clears 4.5:1 on both. */
    .rb-readability-score-over { color: var(--coral-text); }
    .rb-readability-target { font-size: 13px; color: var(--muted); font-style: italic; }
    @media (max-width: 520px) {
      .rb-readability-rows { grid-template-columns: max-content 1fr; row-gap: 2px; }
      .rb-readability-target { grid-column: 1 / -1; padding-bottom: 6px; }
      .rb-readability-row:last-child .rb-readability-target { padding-bottom: 0; }
    }

    .rb-readability-details { margin-top: 8px; font-size: 12px; color: var(--muted); }
    .rb-readability-details summary {
      cursor: pointer; list-style: none;
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 0; color: var(--muted);
    }
    .rb-readability-details summary::-webkit-details-marker { display: none; }
    .rb-readability-details summary::before {
      content: '▸'; transition: transform 0.15s ease; font-size: 10px;
    }
    .rb-readability-details[open] summary::before { transform: rotate(90deg); }
    .rb-readability-details summary:hover { color: var(--ink); }
    .rb-readability-details summary:focus-visible {
      outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px;
    }
    .rb-readability-breakdown {
      margin-top: 6px; padding: 8px 14px;
      background: var(--panel); border-radius: 6px;
      font-size: 12px; color: var(--ink); line-height: 1.7;
    }
    .rb-readability-breakdown div { margin: 0; }

    .rb-subhead { font-family: 'Rethink Sans', sans-serif; font-size: 17px; font-weight: 600; margin: 0 0 4px; color: var(--ink); }
    .rb-subhead-note { font-size: 12px; color: var(--muted); margin-bottom: 12px; font-style: italic; }

    /* ---- Issue cards ---- */
    .rb-issue {
      background: var(--surface); border: 1px solid var(--rule); border-radius: 10px;
      padding: 18px 20px; transition: border-color 0.15s ease;
    }
    .rb-issue:hover { border-color: var(--panel-deep); }
    .rb-issue-head {
      display: flex; justify-content: space-between; align-items: center;
      gap: 10px; margin-bottom: 10px; flex-wrap: wrap;
    }
    .rb-issue-sev { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 700; letter-spacing: 0; }
    .rb-issue-sev .rb-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
    .rb-issue-cat { font-size: 11px; color: var(--muted); font-style: italic; }
    .rb-issue-quote { padding: 12px 16px; border-radius: 6px; font-size: 14px; line-height: 1.55; font-style: italic; margin-bottom: 12px; color: var(--ink); }
    .rb-issue-problem { font-size: 14px; line-height: 1.65; margin-bottom: 12px; color: var(--ink); }
    .rb-issue-suggest { padding: 14px 16px; background: var(--panel); border-radius: 6px; border-left: 3px solid var(--ink); font-size: 14px; line-height: 1.6; color: var(--ink); }
    .rb-issue-suggest-body { font-size: 14px; line-height: 1.6; color: var(--ink); }
    .rb-issue-suggest-label { font-size: 12px; font-weight: 600; letter-spacing: 0; color: var(--ink); margin-bottom: 4px; }

    .rb-prose-para { margin: 0 0 12px; }
    .rb-prose-para:last-child { margin-bottom: 0; }

    /* ---- Flags ---- */
    .rb-flag { background: var(--surface); border: 1px solid var(--rule); border-radius: 8px; padding: 14px 18px; }
    .rb-flag-fw { font-size: 14px; font-weight: 700; color: var(--ink); margin-bottom: 6px; letter-spacing: 0; }
    .rb-flag-text { font-size: 14px; line-height: 1.65; color: var(--ink); }

    /* ---- Rewrite ---- */
    .rb-rewrite-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; gap: 12px; flex-wrap: wrap; }
    .rb-copy-btn {
      background: transparent; border: 1px solid var(--ink); color: var(--ink);
      padding: 6px 14px; border-radius: 6px; font-size: 12px; font-weight: 600;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .rb-copy-btn:hover { background: var(--ink); color: var(--surface); }
    .rb-copy-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .rb-rewrite-body {
      background: var(--surface); border: 1px solid var(--rule); border-radius: 10px;
      padding: 22px 24px; font-size: 15px; line-height: 1.7; white-space: pre-wrap;
      font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    }

    /* ---- Footer ---- */
    .rb-footer {
      padding: 36px 32px 44px; border-top: 1px solid var(--rule);
      margin-top: 36px; background: var(--panel); position: relative;
    }
    .rb-footer::before {
      content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
      background: linear-gradient(90deg, var(--coral) 0 33%, var(--sky) 33% 66%, var(--sage) 66% 100%);
    }
    .rb-footer-inner {
      max-width: 1280px; margin: 0 auto;
      display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 28px;
    }
    @media (max-width: 760px) { .rb-footer-inner { grid-template-columns: 1fr; } .rb-footer { padding: 28px 20px 36px; } }
    .rb-footer-disclaimer { font-size: 13px; color: var(--muted); line-height: 1.65; }
    .rb-footer-disclaimer p { margin: 0 0 10px; }
    .rb-footer-disclaimer strong { color: var(--ink); font-weight: 600; }
    .rb-footer-links { display: flex; flex-direction: column; gap: 8px; font-size: 13px; }
    .rb-footer-links a { color: var(--muted); text-decoration: none; padding: 4px 0; }
    .rb-footer-links a:hover { color: var(--ink); text-decoration: underline; text-underline-offset: 3px; }
    .rb-footer-links a:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }
    .rb-footer-links-label { font-size: 13px; font-weight: 600; letter-spacing: 0; color: var(--ink); margin-bottom: 4px; }
    .rb-footer-feedback-btn {
      background: transparent; border: none; padding: 0;
      font: inherit; color: inherit;
      text-decoration: underline; text-underline-offset: 3px;
      cursor: pointer;
    }
    .rb-footer-feedback-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }
    .rb-footer-meta {
      grid-column: 1 / -1;
      padding-top: 20px; margin-top: 12px;
      border-top: 1px solid var(--panel-deep);
      display: flex; justify-content: space-between; flex-wrap: wrap; gap: 12px;
      font-size: 12px; color: var(--muted); line-height: 1.6;
    }
    .rb-version {
      font-family: 'Rethink Sans', sans-serif;
      font-weight: 600; color: var(--ink);
      font-variant-numeric: tabular-nums;
    }

    /* ---- Shared modal styles (feedback + sign-in + history) ---- */
    .rb-feedback-overlay {
      position: fixed; inset: 0;
      background: rgba(6, 40, 71, 0.5);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px; z-index: 100;
      animation: rb-fade 0.2s ease;
    }
    .rb-feedback-modal, .rb-signin-modal, .rb-history-modal {
      background: var(--surface); border-radius: 12px;
      padding: 28px 28px 24px;
      width: 100%; max-width: 520px; max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 20px 60px rgba(10, 61, 110, 0.25);
      position: relative;
    }
    .rb-signin-modal { max-width: 460px; }
    .rb-history-modal { max-width: 680px; }
    .rb-feedback-header {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 18px;
    }
    .rb-feedback-title { font-size: 22px; margin: 0; color: var(--ink); }
    .rb-feedback-intro {
      font-size: 13px; color: var(--muted); margin: -8px 0 18px; line-height: 1.5;
    }
    .rb-feedback-close {
      background: transparent; border: none;
      font-size: 28px; line-height: 1;
      color: var(--muted); padding: 0 8px;
    }
    .rb-feedback-close:hover { color: var(--ink); }
    .rb-feedback-close:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 4px; }
    .rb-feedback-field { margin-bottom: 16px; }
    .rb-feedback-field label {
      display: block; font-size: 13px; font-weight: 500;
      color: var(--ink); margin-bottom: 6px;
    }
    .rb-feedback-field label .rb-feedback-optional {
      font-weight: 400; color: var(--muted); font-style: italic;
    }
    .rb-feedback-field select,
    .rb-feedback-field input,
    .rb-feedback-field textarea {
      width: 100%; padding: 10px 14px;
      border: 1px solid var(--rule); border-radius: 8px;
      background: var(--surface);
      font-size: 14px; line-height: 1.5; color: var(--ink);
      outline: none; font-family: inherit;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .rb-feedback-field textarea { min-height: 100px; resize: vertical; }
    .rb-feedback-field select:focus,
    .rb-feedback-field input:focus,
    .rb-feedback-field textarea:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(10, 61, 110, 0.15);
    }
    .rb-feedback-char-count {
      font-size: 11px; color: var(--muted); text-align: right; margin-top: 4px;
    }
    .rb-feedback-char-count.rb-over { color: var(--coral-text); font-weight: 600; }
    .rb-feedback-severity-group { display: flex; gap: 8px; flex-wrap: wrap; }
    .rb-feedback-severity-btn {
      flex: 1; min-width: 90px;
      padding: 10px 12px;
      border: 1px solid var(--rule);
      background: var(--surface); color: var(--ink);
      border-radius: 8px;
      font-size: 13px; font-weight: 500;
      transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .rb-feedback-severity-btn:hover:not([aria-pressed="true"]) {
      border-color: var(--ink); background: rgba(10, 61, 110, 0.04);
    }
    /* Selected severity. White text on the saturated logo colours measured
       3.38:1, 2.71:1 and 2.67:1 — all failing. The darkened siblings carry
       white text at 5.4:1, 5.7:1 and 6.2:1. */
    .rb-feedback-severity-btn[aria-pressed="true"][data-severity="blocker"] {
      background: var(--coral-text); border-color: var(--coral-text); color: #FFFFFF;
    }
    .rb-feedback-severity-btn[aria-pressed="true"][data-severity="annoying"] {
      background: var(--sky-text); border-color: var(--sky-text); color: #FFFFFF;
    }
    .rb-feedback-severity-btn[aria-pressed="true"][data-severity="minor"] {
      background: var(--sage-text); border-color: var(--sage-text); color: #FFFFFF;
    }
    .rb-feedback-severity-btn:focus-visible {
      outline: 2px solid var(--primary); outline-offset: 2px;
    }
    .rb-feedback-context {
      background: var(--panel); border-radius: 8px;
      padding: 10px 14px; margin-bottom: 18px; font-size: 13px;
    }
    .rb-feedback-context summary {
      cursor: pointer; font-weight: 500; color: var(--ink);
      list-style: none;
      display: flex; align-items: center; gap: 6px;
    }
    .rb-feedback-context summary::-webkit-details-marker { display: none; }
    .rb-feedback-context summary::before {
      content: '▸'; transition: transform 0.15s ease;
      font-size: 11px; color: var(--muted);
    }
    .rb-feedback-context[open] summary::before { transform: rotate(90deg); }
    .rb-feedback-context summary:hover { color: var(--primary); }
    .rb-feedback-context ul { margin: 10px 0 0; padding-left: 20px; color: var(--muted); }
    .rb-feedback-context li { margin-bottom: 4px; font-size: 12px; word-wrap: break-word; }
    .rb-feedback-error {
      padding: 12px 16px; background: #FCEAE3;
      border: 1px solid var(--coral-text); border-radius: 8px;
      color: var(--harm); font-size: 13px; line-height: 1.55;
      margin-bottom: 14px;
    }
    .rb-feedback-actions {
      display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px;
    }
    .rb-feedback-cancel {
      background: transparent; border: 1px solid var(--rule);
      color: var(--muted);
      padding: 10px 18px; border-radius: 8px;
      font-size: 14px; font-weight: 500;
      transition: border-color 0.15s ease, color 0.15s ease;
    }
    .rb-feedback-cancel:hover { border-color: var(--ink); color: var(--ink); }
    .rb-feedback-cancel:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .rb-feedback-submit {
      background: var(--ink); color: var(--surface); border: none;
      padding: 10px 22px; border-radius: 8px;
      font-size: 14px; font-weight: 600;
      transition: background 0.15s ease;
    }
    .rb-feedback-submit:hover:not(:disabled) { background: var(--ink-deep); }
    .rb-feedback-submit:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .rb-feedback-success { text-align: center; padding: 12px 0 6px; }
    .rb-feedback-success-title {
      font-family: 'Rethink Sans', sans-serif;
      font-weight: 600; font-size: 18px; color: var(--ink); margin: 0 0 8px;
    }
    .rb-feedback-success-body {
      font-size: 14px; color: var(--muted); margin: 0 0 22px; line-height: 1.5;
    }
    .rb-feedback-success-actions {
      display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;
    }

    /* ---- Sign-in modal specifics ---- */
    .rb-signin-email-echo { font-weight: 600; color: var(--ink); word-break: break-all; }
    .rb-signin-note {
      font-size: 12px; color: var(--muted); line-height: 1.5; margin-top: 10px;
    }
    .rb-signin-resend {
      background: transparent; border: none; padding: 0;
      color: var(--muted); font-size: 13px;
      text-decoration: underline; text-underline-offset: 3px; cursor: pointer;
    }
    .rb-signin-resend:hover { color: var(--ink); }
    .rb-signin-resend:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }

    /* ---- History modal ---- */
    .rb-history-list { list-style: none; margin: 0; padding: 0; }
    .rb-history-item {
      border: 1px solid var(--rule); border-radius: 10px;
      padding: 14px 16px; margin-bottom: 10px;
      transition: border-color 0.15s ease;
    }
    .rb-history-item:hover { border-color: var(--panel-deep); }
    .rb-history-item:last-child { margin-bottom: 0; }
    .rb-history-title {
      font-family: 'Rethink Sans', sans-serif;
      font-size: 15px; font-weight: 600; color: var(--ink);
      line-height: 1.35; margin-bottom: 4px;
      overflow-wrap: anywhere;
    }
    .rb-history-meta {
      font-size: 12px; color: var(--muted); line-height: 1.6;
      display: flex; flex-wrap: wrap; gap: 4px 12px; margin-bottom: 10px;
    }
    .rb-history-meta-num { font-variant-numeric: tabular-nums; }
    .rb-history-revision-tag {
      display: inline-block; padding: 1px 8px;
      background: var(--panel); border-radius: 999px;
      font-size: 11px; font-weight: 600; color: var(--ink);
    }
    .rb-history-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .rb-history-action {
      background: transparent; border: 1px solid var(--rule);
      color: var(--ink); padding: 6px 14px; border-radius: 999px;
      font-size: 12px; font-weight: 600;
      transition: border-color 0.15s ease, background 0.15s ease;
    }
    .rb-history-action:hover:not(:disabled) { border-color: var(--ink); background: rgba(10,61,110,0.04); }
    .rb-history-action:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .rb-history-action-primary {
      background: var(--ink); border-color: var(--ink); color: var(--surface);
    }
    .rb-history-action-primary:hover:not(:disabled) { background: var(--ink-deep); color: var(--surface); }
    .rb-history-action-danger { color: var(--harm); border-color: var(--rule); }
    .rb-history-action-danger:hover:not(:disabled) { border-color: var(--harm); }
    .rb-history-empty {
      padding: 28px 20px; text-align: center;
      font-size: 14px; color: var(--muted); line-height: 1.6;
    }
    .rb-history-loading {
      padding: 28px 20px; text-align: center;
      font-size: 14px; color: var(--muted);
    }
    .rb-history-note {
      font-size: 12px; color: var(--muted); line-height: 1.55;
      margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--rule);
    }

    @media (max-width: 600px) {
      .rb-feedback-modal, .rb-signin-modal, .rb-history-modal { padding: 22px 20px 20px; max-height: 95vh; }
      .rb-feedback-actions { flex-direction: column-reverse; }
      .rb-feedback-cancel, .rb-feedback-submit { width: 100%; }
      .rb-history-actions .rb-history-action { flex: 1 1 auto; text-align: center; }
    }

    /* ---- Animation ---- */
    .rb-fade { animation: rb-fade 0.4s ease; }
    @keyframes rb-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
    .rb-dots span { animation: rb-blink 1.4s infinite; opacity: 0.3; }
    .rb-dots span:nth-child(2) { animation-delay: 0.2s; }
    .rb-dots span:nth-child(3) { animation-delay: 0.4s; }
    @keyframes rb-blink { 0%, 80%, 100% { opacity: 0.3; } 40% { opacity: 1; } }
    .rb-phase-active {
      display: inline-block;
      animation: rb-phase-pulse 1.4s ease-in-out infinite;
    }
    @keyframes rb-phase-pulse {
      0%, 100% { opacity: 0.4; transform: scale(1); }
      50%      { opacity: 1;   transform: scale(1.15); }
    }

    /* ---- Reduced motion ---- */
    @media (prefers-reduced-motion: reduce) {
      .rb-fade,
      .rb-dots span,
      .rb-phase-active,
      .rb-feedback-overlay,
      .rb-account-menu {
        animation: none !important;
      }
      *, *::before, *::after {
        transition-duration: 0.01ms !important;
        transition-delay: 0ms !important;
      }
    }
  `;

  const frameworkTone = (idx) => ['coral', 'sky', 'sage', 'coral', 'sky'][idx % 5];

  const readabilityCtx = results?.overall && (results.overall.readingAge || results.overall.smog)
    ? getReadabilityContext(results.overall.readingAge, results.overall.smog, results.overall.contentType)
    : null;

  const feedbackDocLabel = content ? 'text input' : pdfFile ? `PDF (${pdfFile.name})` : 'none';

  const frameworkCount = JURISDICTIONS[jurisdiction].frameworks.length;

  // ---------------------------------------------------------------------------
  // Account control renderers — desktop dropdown and mobile inline block.
  //
  // The upgrade control is the significant change here. It now points at the
  // Stripe Payment Link with the signed-in user's Supabase id attached as
  // client_reference_id, which is the only thing that lets the webhook
  // attribute the payment to an account. When STRIPE_PAYMENT_LINK is empty it
  // falls back to the pricing page, so the control is never broken — just
  // less direct.
  // ---------------------------------------------------------------------------
  const renderDesktopAccount = () => {
    if (!session) {
      return (
        <div className="rb-account">
          <button type="button" onClick={openSignIn} className="rb-signin-btn">
            Sign in
          </button>
        </div>
      );
    }
    return (
      <div className="rb-account">
        <button
          type="button"
          ref={accountTriggerRef}
          onClick={toggleAccountMenu}
          className="rb-account-trigger"
          aria-haspopup="menu"
          aria-expanded={accountMenuOpen}
          aria-label={`Account menu for ${userEmail || 'your account'}, ${tierLabel} tier`}
        >
          <span className="rb-account-avatar" aria-hidden="true">{avatarInitial(userEmail)}</span>
          <span className="rb-account-trigger-email" title={userEmail || ''}>{userEmail}</span>
          <span className={`rb-account-tier${isPro ? ' rb-account-tier-pro' : ''}`}>{tierLabel}</span>
          <span className="rb-account-caret" aria-hidden="true">▾</span>
        </button>

        {accountMenuOpen && (
          <div className="rb-account-menu" role="menu" ref={accountMenuRef} aria-label="Account">
            <div className="rb-account-menu-head">
              <div className="rb-account-menu-email">{userEmail}</div>
              <div className="rb-account-menu-row">
                <span className={`rb-account-tier${isPro ? ' rb-account-tier-pro' : ''}`}>{tierLabel}</span>
                {!isPro && (
                  <a
                    href={upgradeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rb-account-upgrade"
                    role="menuitem"
                    onClick={closeAccountMenu}
                  >
                    Upgrade to Professional →
                  </a>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={isPro ? openHistory : () => { closeAccountMenu(); setProLockMessage(true); }}
              className="rb-account-menu-item"
              role="menuitem"
            >
              Review history
              {!isPro && <span className="rb-account-menu-item-badge">Pro</span>}
            </button>
            <button
              type="button"
              onClick={openFeedback}
              className="rb-account-menu-item"
              role="menuitem"
            >
              Send feedback
            </button>
            <button
              type="button"
              onClick={signOut}
              disabled={signingOut}
              className="rb-account-menu-item rb-account-menu-item-danger"
              role="menuitem"
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMobileAccount = () => {
    if (!session) {
      return (
        <div className="rb-account-mobile">
          <button type="button" onClick={openSignIn} className="rb-signin-btn">
            Sign in
          </button>
        </div>
      );
    }
    return (
      <div className="rb-account-mobile">
        <div className="rb-account-mobile-info">
          <span className="rb-account-mobile-email">{userEmail}</span>
          <span className="rb-account-mobile-row">
            <span className={`rb-account-tier${isPro ? ' rb-account-tier-pro' : ''}`}>
              {tierLabel}
            </span>
            {!isPro && (
              <a
                href={upgradeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rb-account-upgrade"
              >
                Upgrade to Professional →
              </a>
            )}
          </span>
        </div>
        {isPro && (
          <button type="button" onClick={openHistory} className="rb-history-btn">
            Review history
          </button>
        )}
        <button
          type="button" onClick={signOut} disabled={signingOut}
          className="rb-signout-btn"
        >
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Diff panel
  //
  // Four things and no more: readability movement, issue counts by outcome,
  // what the revision introduced, and what it cleared. The introduced list is
  // the point — writers routinely fix a cognitive-load problem and lose the
  // reader's route to a human on the way, or soften a directive and drop the
  // deadline with it. Nothing else here catches that.
  //
  // A lower reading age is better, so the arithmetic sign and the judgement
  // run in opposite directions. rb-diff-better / rb-diff-worse encode the
  // judgement; the words "down" and "up" carry the arithmetic. Colour is
  // never the only signal.
  // ---------------------------------------------------------------------------
  const renderMetric = (label, before, after, delta, lowerIsBetter = true) => {
    if (before == null || after == null) return null;
    const changeText = formatDelta(delta);
    let cls = 'rb-diff-metric-change rb-diff-same';
    if (delta !== 0 && delta != null) {
      const improved = lowerIsBetter ? delta < 0 : delta > 0;
      cls = `rb-diff-metric-change ${improved ? 'rb-diff-better' : 'rb-diff-worse'}`;
    }
    return (
      <div className="rb-diff-metric">
        <div className="rb-diff-metric-label">{label}</div>
        <div className="rb-diff-metric-value">{before} → {after}</div>
        <div className={cls}>{changeText}</div>
      </div>
    );
  };

  const renderDiff = () => {
    if (!diff) return null;

    const issuesDelta = diff.issuesAfter - diff.issuesBefore;

    return (
      <div id="review-diff" className="rb-diff rb-anchor">
        <div className="rb-diff-head">
          <h3 className="rb-diff-title">What changed since the previous version</h3>
          <div className="rb-diff-sub">
            Compared against{' '}
            {revisionOf?.documentTitle || results?.documentTitle || 'the earlier review'}
            {revisionOf?.createdAt ? `, reviewed ${formatReviewDate(revisionOf.createdAt)}` : ''}.
            Reading-age figures are calculated, not estimated, so the movement is arithmetic.
          </div>
        </div>

        <div className="rb-diff-body">
          <div className="rb-diff-metrics">
            {renderMetric('Flesch-Kincaid', diff.fkBefore, diff.fkAfter, diff.fkDelta)}
            {renderMetric('SMOG', diff.smogBefore, diff.smogAfter, diff.smogDelta)}
            <div className="rb-diff-metric">
              <div className="rb-diff-metric-label">Issues</div>
              <div className="rb-diff-metric-value">{diff.issuesBefore} → {diff.issuesAfter}</div>
              <div
                className={`rb-diff-metric-change ${
                  issuesDelta === 0 ? 'rb-diff-same' : issuesDelta < 0 ? 'rb-diff-better' : 'rb-diff-worse'
                }`}
              >
                {formatDelta(issuesDelta)}
              </div>
            </div>
          </div>

          <div className="rb-diff-cats">
            {diff.categories
              .filter((c) => c.before > 0 || c.after > 0)
              .map((c) => (
                <span key={c.value} className="rb-diff-cat-pill">
                  {c.label} {c.before} → {c.after}
                </span>
              ))}
          </div>

          {diff.introduced.length > 0 && (
            <div className="rb-diff-group">
              <div className="rb-diff-group-label">
                <span className="rb-diff-count rb-diff-count-introduced">{diff.introduced.length}</span>
                Introduced by this revision
              </div>
              <ul className="rb-diff-list rb-diff-list-introduced">
                {diff.introduced.map((issue, i) => (
                  <li key={i}>
                    <span className="rb-diff-cat">{categoryLabel(issue.category)}</span>
                    <span className="rb-diff-excerpt">"{issue.excerpt}"</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {diff.persisted.length > 0 && (
            <div className="rb-diff-group">
              <div className="rb-diff-group-label">
                <span className="rb-diff-count rb-diff-count-persisted">{diff.persisted.length}</span>
                Still present
              </div>
              <ul className="rb-diff-list">
                {diff.persisted.map((issue, i) => (
                  <li key={i}>
                    <span className="rb-diff-cat">{categoryLabel(issue.category)}</span>
                    <span className="rb-diff-excerpt">"{issue.excerpt}"</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {diff.cleared.length > 0 && (
            <div className="rb-diff-group">
              <div className="rb-diff-group-label">
                <span className="rb-diff-count rb-diff-count-cleared">{diff.cleared.length}</span>
                Cleared
              </div>
              <ul className="rb-diff-list rb-diff-list-cleared">
                {diff.cleared.map((issue, i) => (
                  <li key={i}>
                    <span className="rb-diff-cat">{categoryLabel(issue.category)}</span>
                    <span className="rb-diff-excerpt">"{issue.excerpt}"</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(diff.flagsRaised.length > 0 || diff.flagsCleared.length > 0) && (
            <div className="rb-diff-group">
              <div className="rb-diff-group-label">Framework flags</div>
              <ul className="rb-diff-list">
                {diff.flagsRaised.map((f) => (
                  <li key={`raised-${f}`}>New: {f}</li>
                ))}
                {diff.flagsCleared.map((f) => (
                  <li key={`cleared-${f}`}>Cleared: {f}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="rb-diff-note">
            Issues are matched on the exact phrase and category. A problem you reworded
            without fixing will show as one cleared and one introduced rather than as
            persisting — visible in both columns rather than quietly reconciled.
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rb-root">
      <style>{css}</style>

      <a href="#main" className="rb-skip">Skip to main content</a>

      <div role="status" aria-live="polite" aria-atomic="true" className="rb-sr-only">
        {announcement}
      </div>

      <header className="rb-header">
        <div className="rb-header-inner">
          <div className="rb-header-row">
            <a href="/" className="rb-brand" aria-label="Rembrandt Editor — home">
              <img src="/logo.png" alt="" className="rb-logo" aria-hidden="true" />
              <span className="rb-brand-text">
                <span className="rb-wordmark">Rembrandt Editor</span>
                <span className="rb-tagline">Trauma-informed content review</span>
              </span>
            </a>

            <div className="rb-header-right">
              <nav id="rb-nav" className={`rb-nav${mobileNavOpen ? ' rb-nav-open' : ''}`} aria-label="Main">
                {(mobileNavOpen ? NAV_LINKS : DESKTOP_NAV_LINKS).map((link) => (
                  <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">
                    {link.label}
                  </a>
                ))}
                {mobileNavOpen && (
                  <>
                    <button type="button" onClick={openFeedback} className="rb-nav-feedback-btn">
                      Send feedback
                    </button>
                    {renderMobileAccount()}
                  </>
                )}
              </nav>

              {renderDesktopAccount()}

              <button
                type="button" className="rb-nav-toggle"
                aria-expanded={mobileNavOpen} aria-controls="rb-nav"
                onClick={() => setMobileNavOpen(o => !o)}
              >
                {mobileNavOpen ? 'Close' : 'Menu'}
              </button>
            </div>
          </div>
        </div>

        <div className="rb-lens-row">
          <div className="rb-jur-group" role="group" aria-label="Jurisdiction">
            {Object.entries(JURISDICTIONS).map(([key, { short, label }]) => (
              <button
                key={key} onClick={() => setJurisdiction(key)}
                aria-pressed={jurisdiction === key} aria-label={label}
                className="rb-jur-btn"
              >
                {short}
              </button>
            ))}
          </div>

          <details className="rb-fw-details">
            <summary className="rb-fw-summary" aria-label={`Show the ${frameworkCount} frameworks applied for ${JURISDICTIONS[jurisdiction].label}`}>
              <strong>{frameworkCount}</strong> {JURISDICTIONS[jurisdiction].short} frameworks
            </summary>
            <ul className="rb-fw-list" aria-label="Frameworks applied" aria-live="polite">
              {JURISDICTIONS[jurisdiction].frameworks.map((fw, i) => (
                <li key={fw.name} className="rb-fw" data-tone={frameworkTone(i)}>
                  <a href={fw.url} target="_blank" rel="noopener noreferrer" className="rb-fw-link">
                    {fw.name}
                  </a>
                </li>
              ))}
            </ul>
          </details>
        </div>
      </header>

      {!aboutDismissed && (
        <section className="rb-about" aria-label="About Rembrandt Editor">
          <div className="rb-about-inner">
            <button type="button" onClick={dismissAbout} aria-label="Dismiss this notice" className="rb-about-close">
              Got it
            </button>

            <div className="rb-about-grid">
              <div className="rb-about-block">
                <h2>What this is</h2>
                <p className="rb-about-body">
                  Rembrandt Editor checks content for readers in <strong>living experience</strong>. That means people moving through grief, fear, pain or exhaustion, or simply having a difficult day. It reviews against trauma-informed principles and the regulatory frameworks that apply where you publish.
                </p>
                <p className="rb-about-body">
                  Useful whether you are writing the content, editing it, publishing it or trying to understand one you have received.
                </p>
              </div>
              <div className="rb-about-block">
                <h2>What this isn't</h2>
                <p className="rb-about-body">
                  A compliance audit, a legal adjudicator or a substitute for testing with the people the content is for. It surfaces plausible concerns. You decide what to do about them.
                </p>
              </div>
            </div>

            <div className="rb-about-meta">
              Built by <strong>Adrie van der Luijt</strong>, a senior content designer with four decades in government, compliance and trauma-informed practice. <a href={`${SITE}/about-adrie-van-der-luijt/`} target="_blank" rel="noopener noreferrer" className="rb-about-meta-link">Read more →</a>
            </div>
          </div>
        </section>
      )}

      <main id="main" className="rb-main">
        <section aria-labelledby="input-heading">
          <h2 id="input-heading" className="rb-display rb-section-title">Content to review</h2>

          {revisionOf && (
            <div className="rb-revision-banner" role="status">
              <div className="rb-revision-banner-body">
                <span className="rb-revision-banner-label">Revising a previous version</span>
                <span className="rb-revision-banner-title">
                  {revisionOf.documentTitle || 'Earlier review'}
                </span>
                {revisionOf.createdAt ? `, reviewed ${formatReviewDate(revisionOf.createdAt)}` : ''}.
                {' '}The next review will show what your edits changed.
              </div>
              <button type="button" onClick={cancelRevision} className="rb-revision-cancel">
                Not a revision
              </button>
            </div>
          )}

          <details className="rb-context-details">
            <summary>
              {hasContextSet ? 'Context (set) — edit' : 'Add context (optional)'}
            </summary>

            <div className="rb-title-field">
              <label htmlFor="doc-title-input" className="rb-field-label">
                Name this document
              </label>
              <input
                id="doc-title-input"
                type="text"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value.slice(0, DOC_TITLE_MAX))}
                className="rb-title-input"
                placeholder="e.g. 'Arrears letter — second reminder' or 'Bereavement page v3'"
                maxLength={DOC_TITLE_MAX}
                aria-describedby="doc-title-help"
              />
              <div id="doc-title-help" className="rb-sr-only">
                An optional label so you can recognise this review later in your history. Left empty, the first line of your content is used.
              </div>
            </div>

            <div className="rb-role">
              <span className="rb-field-label" id="role-label">
                Your role with this content
              </span>
              <div className="rb-chips" role="group" aria-labelledby="role-label">
                {ROLE_CHIPS.map((chip) => (
                  <button
                    key={chip} type="button" onClick={() => toggleChip(chip)}
                    aria-pressed={role === chip} className="rb-chip"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>

            <div className="rb-notes">
              <label htmlFor="notes-input" className="rb-field-label">
                Anything else we should know
              </label>
              <textarea
                id="notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="rb-notes-input"
                placeholder="e.g. 'audience has limited English' or 'I can't change the legal disclaimer at the bottom'"
                aria-describedby="notes-help"
              />
              <div id="notes-help" className="rb-sr-only">
                Anything you tell Rembrandt Editor here will be factored into the review and shown back to you alongside the result so you can verify it was understood.
              </div>
            </div>

            <div className="rb-pro-section">
              <div className="rb-pro-header">
                <span className="rb-pro-heading">Drafting context</span>
                <span className={`rb-pro-badge${isPro ? ' rb-pro-badge-active' : ''}`}>
                  {isPro ? 'Pro' : 'Pro only'}
                </span>
              </div>

              <div className="rb-pro-row">
                <div className="rb-pro-field rb-pro-field-narrow">
                  <label htmlFor="target-age-select" className="rb-field-label">
                    Target reading age
                  </label>
                  <select
                    id="target-age-select"
                    value={targetReadingAge ?? ''}
                    onChange={(e) => selectTargetAge(e.target.value)}
                    className={`rb-pro-select${!isPro ? ' rb-pro-locked' : ''}`}
                    aria-describedby={!isPro ? 'pro-lock-help' : undefined}
                  >
                    <option value="">Auto-detect</option>
                    {TARGET_AGE_OPTIONS.map((age) => (
                      <option key={age} value={age}>Age {age}</option>
                    ))}
                  </select>
                </div>

                <div className="rb-pro-field">
                  <span className="rb-field-label" id="urgency-label">
                    Content urgency
                  </span>
                  <div className="rb-chips" role="group" aria-labelledby="urgency-label">
                    {URGENCY_LEVELS.map((level) => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => selectUrgency(level.value)}
                        aria-pressed={urgency === level.value}
                        className={`rb-chip${!isPro ? ' rb-pro-locked' : ''}`}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rb-pro-field">
                <span className="rb-field-label" id="audience-state-label">
                  Audience state (select any that apply)
                </span>
                <div className="rb-chips" role="group" aria-labelledby="audience-state-label">
                  {AUDIENCE_STATES.map((state) => {
                    const isSelected = audienceStates.includes(state.value);
                    return (
                      <button
                        key={state.value}
                        type="button"
                        onClick={() => toggleAudienceState(state.value)}
                        aria-pressed={isSelected}
                        className={`rb-chip${!isPro ? ' rb-pro-locked' : ''}`}
                      >
                        {state.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Retention consent. Off by default. The note is deliberately
                  honest that excerpts are stored regardless — a review without
                  excerpts is not a review, and implying otherwise would be the
                  sort of unverifiable institutional reassurance the tool
                  itself flags. */}
              <div className="rb-retain">
                <input
                  type="checkbox"
                  id="retain-source"
                  checked={isPro && saveSourceText}
                  onChange={toggleSaveSourceText}
                  className={!isPro ? 'rb-pro-locked' : undefined}
                  aria-describedby="retain-source-note"
                />
                <label htmlFor="retain-source" className="rb-retain-label">
                  Keep the full text with this review
                  <span className="rb-retain-note" id="retain-source-note">
                    Needed to load this document back in later and compare a revision against it.
                    Quoted phrases from your content are stored either way, because the review
                    itself is built from them. Reviews are kept for 90 days, and you can delete
                    any of them from your history.
                  </span>
                </label>
              </div>

              {!isPro && proLockMessage && (
                <div className="rb-pro-upsell" id="pro-lock-help" role="status">
                  Drafting context, review history and version comparison are part of Rembrandt Editor Professional. Context shapes the review around your actual audience — distress, grief, financial difficulty — rather than letting content-type detection guess, and history lets you see what your edits changed.
                  {' '}
                  <a
                    href={upgradeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rb-pro-upsell-link"
                  >
                    {STRIPE_PAYMENT_LINK && userId ? 'Upgrade to Professional →' : 'Find out about Professional →'}
                  </a>
                </div>
              )}
            </div>
          </details>

          {pdfFile ? (
            <div className="rb-pdf-card" role="region" aria-label="PDF loaded">
              <div className="rb-pdf-icon" aria-hidden="true">📄</div>
              <div>
                <div className="rb-pdf-name">{pdfFile.name}</div>
                <div className="rb-pdf-meta">
                  {formatFileSize(pdfFile.size)}
                  <span className="rb-pdf-meta-tag">PDF</span>
                </div>
              </div>
              <button type="button" onClick={clearPdf} className="rb-pdf-remove">
                Remove and use text instead
              </button>
            </div>
          ) : (
            <>
              <label htmlFor="content-input" className="rb-sr-only">Paste the content you want reviewed</label>
              <textarea
                id="content-input" ref={textareaRef}
                value={content} onChange={(e) => setContent(e.target.value)}
                placeholder="Paste the letter, email, web page, notice, error message or policy passage you want reviewed..."
                className="rb-textarea"
                aria-describedby="content-help char-count"
                aria-invalid={overLimit}
                maxLength={CHAR_LIMIT + 500}
              />

              <div className="rb-meta-row">
                <div id="char-count" aria-live="polite">
                  {overLimit ? (
                    <span className="rb-over">{(-charsLeft).toLocaleString()} characters over limit</span>
                  ) : (
                    <span>{content.length.toLocaleString()} of {CHAR_LIMIT.toLocaleString()} characters</span>
                  )}
                </div>
                <div>
                  {content ? (
                    <button onClick={clear} className="rb-link-btn">Clear</button>
                  ) : (
                    <button onClick={loadExample} className="rb-link-btn">Try an example</button>
                  )}
                </div>
              </div>

              <div className="rb-pdf-toggle">
                <span>or</span>
                <button
                  type="button" className="rb-link-btn"
                  onClick={() => pdfInputRef.current?.click()}
                >
                  upload a PDF
                </button>
                <span className="rb-pdf-toggle-tag">Beta</span>
                <input
                  ref={pdfInputRef} type="file" accept="application/pdf"
                  onChange={handlePdfUpload}
                  style={{ display: 'none' }}
                  aria-label="Upload PDF"
                />
              </div>
            </>
          )}

          <div id="content-help" className="rb-sr-only">
            Paste up to {CHAR_LIMIT.toLocaleString()} characters of content, or upload a PDF up to {(PDF_MAX_BYTES / 1024 / 1024).toFixed(1)} megabytes.
          </div>

          <div className="rb-actions">
            <button
              onClick={analyse}
              disabled={(!content.trim() && !pdfFile) || loading || overLimit}
              className="rb-primary-btn"
            >
              {loading ? (
                <>Reading carefully<span className="rb-dots" aria-hidden="true"><span>.</span><span>.</span><span>.</span></span></>
              ) : revisionOf ? (
                <>Review the revision and compare</>
              ) : (
                <>Review using {JURISDICTIONS[jurisdiction].short} frameworks</>
              )}
            </button>
          </div>

          {aboutDismissed && (
            <button type="button" onClick={() => setAboutDismissed(false)} className="rb-about-show">
              About Rembrandt Editor
            </button>
          )}
        </section>

        <section aria-labelledby="results-heading" aria-busy={loading}>
          <h2
            id="results-heading" ref={resultsHeadingRef} tabIndex={-1}
            className="rb-display rb-section-title"
          >
            Review
          </h2>

          {!results && !loading && !error && (
            <div className="rb-empty">
              <div className="rb-empty-inner">
                <p className="rb-empty-quote">
                  "We design for full capacity. Life rarely provides it."
                </p>
                <p className="rb-empty-body">
                  Rembrandt Editor reads for the person who is tired, frightened, grieving, in pain or simply having a difficult day. That is most readers, most of the time.
                </p>
                <div className="rb-empty-divider" aria-hidden="true" />
                <p className="rb-empty-body">
                  <a href={`${SITE}/rembrandt-editor-plus/`} target="_blank" rel="noopener noreferrer"><i>Team plans →</i></a>
                </p>
              </div>
            </div>
          )}

          {loading && (
            <div className="rb-loading rb-fade">
              <div className="rb-loading-inner">
                <div className="rb-display rb-loading-title">
                  {elapsedSeconds < 30
                    ? 'Reading carefully'
                    : 'Still reading — longer passages can take a minute or two'}
                </div>
                <ul className="rb-phases" aria-label="Review progress">
                  {PHASES.map((p, i) => {
                    const done = i < reviewPhase;
                    const current = i === reviewPhase;
                    const cls = done ? 'rb-phase rb-phase-done' : current ? 'rb-phase rb-phase-current' : 'rb-phase rb-phase-pending';
                    return (
                      <li key={i} className={cls}>
                        <span className="rb-phase-marker" aria-hidden="true">
                          {done ? '✓' : current ? <span className="rb-phase-active">●</span> : '·'}
                        </span>
                        {p.label}
                      </li>
                    );
                  })}
                </ul>
                <div className="rb-loading-elapsed" aria-live="off">
                  {elapsedSeconds < 60
                    ? `${elapsedSeconds}s elapsed`
                    : `${Math.floor(elapsedSeconds / 60)}m ${String(elapsedSeconds % 60).padStart(2, '0')}s elapsed`}
                </div>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="rb-error-card rb-fade" role="alert">{error}</div>
          )}

          {results && !loading && (
            <div ref={resultsRef} className="rb-results rb-fade">
              <div className="rb-results-actions">
                <button onClick={copyFullReview} className="rb-results-copy">
                  {reviewCopied ? 'Copied' : 'Copy full review'}
                </button>
              </div>

              <nav className="rb-results-nav" aria-label="Jump to section">
                {diff && <a href="#review-diff">What changed</a>}
                <a href="#review-summary">Summary</a>
                {results.issues?.length > 0 && (
                  <a href="#review-issues">
                    Issues<span className="rb-nav-count">({results.issues.length})</span>
                  </a>
                )}
                {results.jurisdictionFlags?.length > 0 && (
                  <a href="#review-flags">
                    {JURISDICTIONS[jurisdiction].short} flags<span className="rb-nav-count">({results.jurisdictionFlags.length})</span>
                  </a>
                )}
                {results.rewrite && <a href="#review-rewrite">Rewrite</a>}
              </nav>

              {renderDiff()}

              {results.overall && (
                <div id="review-summary" className="rb-verdict rb-anchor">
                  {results.overall.contentType && (
                    <div className="rb-verdict-detected">
                      Detected as: <strong>{results.overall.contentType}</strong>
                    </div>
                  )}
                  {results.overall.contextApplied && (
                    <div className="rb-verdict-context">
                      <strong>Context applied</strong>
                      {results.overall.contextApplied}
                    </div>
                  )}
                  {results.overall.worksStructurally && (
                    <div className="rb-verdict-works">
                      <div className="rb-verdict-works-label">What works structurally</div>
                      <ProseField
                        text={results.overall.worksStructurally}
                        className="rb-verdict-works-body"
                      />
                    </div>
                  )}
                  <ProseField text={results.overall.summary} className="rb-verdict-summary" />
                  {(results.overall.readingAge || results.overall.smog) && (
                    <div className="rb-verdict-meta">
                      <div className="rb-readability-heading">Reading age</div>

                      <div className="rb-readability-rows" role="table" aria-label="Readability scores">
                        {results.overall.readingAge && (
                          <div className="rb-readability-row" role="row">
                            <span className="rb-readability-name" role="cell">Flesch-Kincaid</span>
                            <span
                              className={`rb-readability-score${readabilityCtx?.fkExceedsTarget ? ' rb-readability-score-over' : ''}`}
                              role="cell"
                            >
                              {results.overall.readingAge}
                            </span>
                            <span className="rb-readability-target" role="cell">
                              {readabilityCtx?.fkTargetText ? `Target: ${readabilityCtx.fkTargetText}` : ''}
                            </span>
                          </div>
                        )}
                        {results.overall.smog && (
                          <div className="rb-readability-row" role="row">
                            <span className="rb-readability-name" role="cell">SMOG</span>
                            <span
                              className={`rb-readability-score${readabilityCtx?.smogExceedsTarget ? ' rb-readability-score-over' : ''}`}
                              role="cell"
                            >
                              {results.overall.smog}
                            </span>
                            <span className="rb-readability-target" role="cell">
                              {readabilityCtx?.smogTargetText ? `Target: ${readabilityCtx.smogTargetText}` : ''}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="rb-verdict-meta-note">
                        Flesch-Kincaid (F-K) measures sentence load. SMOG measures vocabulary density and is the NHS healthcare standard.
                        {readabilityCtx?.isLivingExperience && readabilityCtx.exceedsTarget &&
                          ' Lower is better for content read in distress.'}
                      </div>

                      {results.overall.readabilityBreakdown && (
                        <details className="rb-readability-details">
                          <summary>Show calculation</summary>
                          <div className="rb-readability-breakdown">
                            <div>{results.overall.readabilityBreakdown.words.toLocaleString()} words · {results.overall.readabilityBreakdown.sentences.toLocaleString()} sentences</div>
                            <div>{results.overall.readabilityBreakdown.wordsPerSentence} words per sentence (average)</div>
                            <div>{results.overall.readabilityBreakdown.syllablesPerWord} syllables per word (average)</div>
                            <div>{results.overall.readabilityBreakdown.polysyllables.toLocaleString()} polysyllabic words (3+ syllables)</div>
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              )}

              {results.issues?.length > 0 && (
                <div id="review-issues" className="rb-anchor">
                  <h3 className="rb-display rb-subhead">
                    Specific issues ({results.issues.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {results.issues.map((issue, i) => {
                      const s = severityMeta(issue.severity);
                      return (
                        <article key={i} className="rb-issue" aria-labelledby={`issue-${i}-label`}>
                          <div className="rb-issue-head">
                            <span className="rb-issue-sev" id={`issue-${i}-label`}>
                              <span className="rb-dot" style={{ background: s.dot }} aria-hidden="true" />
                              <span style={{ color: s.text }}>{s.label}</span>
                            </span>
                            <span className="rb-issue-cat">{categoryLabel(issue.category)}</span>
                          </div>
                          <blockquote className="rb-issue-quote" style={{ background: s.bg }}>
                            "{issue.excerpt}"
                          </blockquote>
                          <ProseField
                            text={issue.observation || issue.problem}
                            className="rb-issue-problem"
                          />
                          <div className="rb-issue-suggest">
                            <div className="rb-issue-suggest-label">Try instead</div>
                            <ProseField text={issue.suggestion} className="rb-issue-suggest-body" />
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}

              {results.jurisdictionFlags?.length > 0 && (
                <div id="review-flags" className="rb-anchor">
                  <h3 className="rb-display rb-subhead">{JURISDICTIONS[jurisdiction].short} flags</h3>
                  <div className="rb-subhead-note">
                    Plausible concerns under named frameworks. Not a compliance audit.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {results.jurisdictionFlags.map((flag, i) => (
                      <div key={i} className="rb-flag">
                        <div className="rb-flag-fw">{flag.framework}</div>
                        <div className="rb-flag-text">{flag.concern}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results.rewrite && (
                <div id="review-rewrite" className="rb-anchor">
                  <div className="rb-rewrite-head">
                    <h3 className="rb-display rb-subhead" style={{ marginBottom: 0 }}>Suggested rewrite</h3>
                    <button onClick={copyRewrite} className="rb-copy-btn">{copied ? 'Copied' : 'Copy rewrite'}</button>
                  </div>
                  <div className="rb-rewrite-body">{results.rewrite}</div>
                </div>
              )}
            </div>
          )}

          <a
            href={`${SITE}/help/`}
            target="_blank"
            rel="noopener noreferrer"
            className="rb-help-link"
          >
            Help with Rembrandt Editor
          </a>
        </section>
      </main>

      <footer className="rb-footer">
        <div className="rb-footer-inner">
          <div className="rb-footer-disclaimer">
            <p>
              <strong>Rembrandt Editor</strong> reviews content through a trauma-informed lens. It is not a compliance tool, a legal adjudicator or a replacement for testing with the people the content is for. It flags plausible concerns. You decide what to do about them.
            </p>
            <p>
              Built and maintained by <a href="https://traumainformedcontent.com" target="_blank" rel="noopener noreferrer">Trauma-Informed Content Consulting</a>. <button type="button" onClick={openFeedback} className="rb-footer-feedback-btn">Send feedback</button>.
            </p>
          </div>

          <div className="rb-footer-links">
            <div className="rb-footer-links-label">More from us</div>
            <a href={`${SITE}/`} target="_blank" rel="noopener noreferrer">traumainformedcontent.com</a>
            <a href={`${SITE}/what-is-trauma-informed-content/`} target="_blank" rel="noopener noreferrer">What is trauma-informed content?</a>
            <a href={`${SITE}/resources/`} target="_blank" rel="noopener noreferrer">Resources</a>
            <a href={`${SITE}/help/`} target="_blank" rel="noopener noreferrer">Help</a>
            <a href={`${SITE}/accessibility/`} target="_blank" rel="noopener noreferrer">Accessibility statement</a>
            <a href={`${SITE}/rembrandt-privacy/`} target="_blank" rel="noopener noreferrer">Privacy policy</a>
            <a href={`${SITE}/rembrandt-dpa/`} target="_blank" rel="noopener noreferrer">Data protection agreement</a>            
            <a href={`${SITE}/rembrandt-terms/`} target="_blank" rel="noopener noreferrer">Terms of service</a>
            <a href={`${SITE}/about-rembrandt/`} target="_blank" rel="noopener noreferrer">About</a>
            <a href={`${SITE}/rembrandt-editor-plus/`} target="_blank" rel="noopener noreferrer">Pricing</a>
            <a href={`${SITE}/contact-us/`} target="_blank" rel="noopener noreferrer">Contact</a>
          </div>

          <div className="rb-footer-meta">
            <div>
              © {new Date().getFullYear()} <a href="https://traumainformedcontent.com" target="_blank" rel="noopener noreferrer">Trauma-Informed Content Consulting</a>, a trading name of <a href="https://www.banksidecommunications.com/" target="_blank" rel="noopener noreferrer">Bankside Communications Limited</a>. All rights reserved.
            </div>
            <div className="rb-version" aria-label={`Version ${VERSION}, released ${VERSION_DATE}`}>
              {VERSION} · {VERSION_DATE}
            </div>
          </div>
        </div>
      </footer>

      {/* ---- Feedback modal ---- */}
      {feedbackOpen && (
        <div className="rb-feedback-overlay" onClick={closeFeedback}>
          <div
            className="rb-feedback-modal"
            role="dialog"
            aria-labelledby="feedback-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-feedback-header">
              <h2 id="feedback-title" className="rb-display rb-feedback-title">
                {feedbackSent ? 'Feedback sent' : 'Send feedback'}
              </h2>
              <button
                type="button" onClick={closeFeedback}
                aria-label="Close feedback form" className="rb-feedback-close"
              >×</button>
            </div>

            {feedbackSent ? (
              <div className="rb-feedback-success">
                <p className="rb-feedback-success-title">Thanks — that's logged.</p>
                <p className="rb-feedback-success-body">
                  You can close this, or send another bit of feedback if you have more.
                </p>
                <div className="rb-feedback-success-actions">
                  <button type="button" onClick={sendAnother} className="rb-feedback-cancel">
                    Send another
                  </button>
                  <button type="button" onClick={closeFeedback} className="rb-feedback-submit">
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="rb-feedback-intro">
                  Tell us what's working, what isn't or what's missing. This goes straight to the team — no public form, no autoresponder.
                </p>

                <div className="rb-feedback-field">
                  <label htmlFor="feedback-category">What kind of feedback is this?</label>
                  <select
                    id="feedback-category"
                    ref={feedbackFirstFieldRef}
                    value={feedbackCategory}
                    onChange={(e) => setFeedbackCategory(e.target.value)}
                  >
                    <option value="">Choose one...</option>
                    {FEEDBACK_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div className="rb-feedback-field">
                  <span className="rb-feedback-label" id="feedback-severity-label" style={{display: 'block', fontSize: 13, fontWeight: 500, color: PALETTE.ink, marginBottom: 6}}>
                    How serious is it?
                  </span>
                  <div className="rb-feedback-severity-group" role="group" aria-labelledby="feedback-severity-label">
                    {FEEDBACK_SEVERITIES.map(s => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => setFeedbackSeverity(s.value)}
                        aria-pressed={feedbackSeverity === s.value}
                        data-severity={s.value}
                        className="rb-feedback-severity-btn"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rb-feedback-field">
                  <label htmlFor="feedback-message">What happened, or what would you like?</label>
                  <textarea
                    id="feedback-message"
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder="Be as specific as you can. If it's about a particular review, what content were you looking at and what was off?"
                    maxLength={FEEDBACK_MESSAGE_MAX + 100}
                  />
                  <div className={`rb-feedback-char-count${feedbackMessage.length > FEEDBACK_MESSAGE_MAX ? ' rb-over' : ''}`}>
                    {feedbackMessage.length > FEEDBACK_MESSAGE_MAX
                      ? `${feedbackMessage.length - FEEDBACK_MESSAGE_MAX} characters over limit`
                      : `${feedbackMessage.length} / ${FEEDBACK_MESSAGE_MAX}`}
                  </div>
                </div>

                <div className="rb-feedback-field">
                  <label htmlFor="feedback-email">
                    Your email <span className="rb-feedback-optional">(optional — only if you'd like a reply)</span>
                  </label>
                  <input
                    id="feedback-email"
                    type="email"
                    value={feedbackEmail}
                    onChange={(e) => setFeedbackEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>

                <details className="rb-feedback-context">
                  <summary>What gets sent with this</summary>
                  <ul>
                    <li>Document being reviewed: {feedbackDocLabel}</li>
                    <li>Jurisdiction selected: {jurisdiction}</li>
                    <li>App version: {VERSION}</li>
                    <li>Your browser (for debugging)</li>
                  </ul>
                </details>

                {feedbackError && (
                  <div className="rb-feedback-error" role="alert">{feedbackError}</div>
                )}

                <div className="rb-feedback-actions">
                  <button type="button" onClick={closeFeedback} className="rb-feedback-cancel">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitFeedback}
                    disabled={!canSubmitFeedback || feedbackMessage.length > FEEDBACK_MESSAGE_MAX}
                    className="rb-feedback-submit"
                  >
                    {feedbackSubmitting ? 'Sending...' : 'Send feedback'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---- Sign-in modal ---- */}
      {signInOpen && (
        <div className="rb-feedback-overlay" onClick={closeSignIn}>
          <div
            className="rb-signin-modal"
            role="dialog"
            aria-labelledby="signin-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-feedback-header">
              <h2 id="signin-title" className="rb-display rb-feedback-title">
                {signInSent ? 'Check your email' : 'Sign in'}
              </h2>
              <button
                type="button" onClick={closeSignIn}
                aria-label="Close sign-in form" className="rb-feedback-close"
              >×</button>
            </div>

            {signInSent ? (
              <div className="rb-feedback-success" style={{ textAlign: 'left' }}>
                <p className="rb-feedback-success-body" style={{ marginBottom: 14 }}>
                  We've sent a sign-in link to{' '}
                  <span className="rb-signin-email-echo">{signInEmail.trim()}</span>. Open the email and click the link to finish signing in. The link works once and expires after a short while.
                </p>
                <p className="rb-signin-note">
                  No email after a minute or two? Check your spam folder, or{' '}
                  <button type="button" onClick={resendSignIn} className="rb-signin-resend">
                    try a different address
                  </button>.
                </p>
                <div className="rb-feedback-actions">
                  <button type="button" onClick={closeSignIn} className="rb-feedback-submit">
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="rb-feedback-intro">
                  Enter your email and we'll send you a sign-in link — no password to remember. Click the link in your inbox and you're in. If you've bought Professional, signing in is how Rembrandt knows to apply it.
                </p>

                <div className="rb-feedback-field">
                  <label htmlFor="signin-email">Email address</label>
                  <input
                    id="signin-email"
                    ref={signInFirstFieldRef}
                    type="email"
                    value={signInEmail}
                    onChange={(e) => setSignInEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canSubmitSignIn) {
                        e.preventDefault();
                        submitSignIn();
                      }
                    }}
                    placeholder="you@example.com"
                    autoComplete="email"
                    aria-describedby="signin-help"
                  />
                  <div id="signin-help" className="rb-signin-note">
                    We'll only use this to send the sign-in link and, if you're a Professional subscriber, to match your plan.
                  </div>
                </div>

                {signInError && (
                  <div className="rb-feedback-error" role="alert">{signInError}</div>
                )}

                <div className="rb-feedback-actions">
                  <button type="button" onClick={closeSignIn} className="rb-feedback-cancel">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitSignIn}
                    disabled={!canSubmitSignIn}
                    className="rb-feedback-submit"
                  >
                    {signInSubmitting ? 'Sending link…' : 'Send me a link'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---- Review history modal ----
          Professional and Team only. Three actions per stored review:

            Open              — render it back into the results pane. The stored
                                shape matches a live review exactly, so no
                                separate renderer exists for it.
            Review a revision — load its source back into the editor, carry the
                                jurisdiction and context forward, and mark the
                                next review as a revision of it. This is the
                                interaction the diff exists for.
            Delete            — hard delete. Not a soft flag; a soft-deleted
                                review is still a retained review.

          "Review a revision" is deliberately the primary action. Opening an old
          review is looking backwards; comparing a new draft against it is the
          thing that changes what the writer does next. */}
      {historyOpen && (
        <div className="rb-feedback-overlay" onClick={closeHistory}>
          <div
            className="rb-history-modal"
            role="dialog"
            aria-labelledby="history-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-feedback-header">
              <h2 id="history-title" className="rb-display rb-feedback-title">
                Review history
              </h2>
              <button
                type="button" onClick={closeHistory}
                aria-label="Close review history" className="rb-feedback-close"
              >×</button>
            </div>

            {historyError && (
              <div className="rb-feedback-error" role="alert">{historyError}</div>
            )}

            {historyLoading ? (
              <div className="rb-history-loading">Loading your reviews…</div>
            ) : historyItems.length === 0 ? (
              <div className="rb-history-empty">
                Nothing here yet. Reviews you run while signed in to Professional are
                saved automatically, and appear here so you can compare a revision
                against them.
              </div>
            ) : (
              <ul className="rb-history-list">
                {historyItems.map((item) => (
                  <li key={item.id} className="rb-history-item">
                    <div className="rb-history-title">
                      {item.documentTitle || item.contentType || 'Untitled review'}
                    </div>
                    <div className="rb-history-meta">
                      <span>{formatReviewDate(item.createdAt)}</span>
                      <span>{item.jurisdiction} lens</span>
                      {item.readingAge != null && (
                        <span className="rb-history-meta-num">F-K {item.readingAge}</span>
                      )}
                      {item.smog != null && (
                        <span className="rb-history-meta-num">SMOG {item.smog}</span>
                      )}
                      <span className="rb-history-meta-num">
                        {item.issueCount} issue{item.issueCount === 1 ? '' : 's'}
                      </span>
                      {item.parentReviewId && (
                        <span className="rb-history-revision-tag">Revision</span>
                      )}
                    </div>
                    <div className="rb-history-actions">
                      <button
                        type="button"
                        onClick={() => startRevision(item.id)}
                        disabled={historyBusyId === item.id}
                        className="rb-history-action rb-history-action-primary"
                      >
                        {historyBusyId === item.id ? 'Working…' : 'Review a revision'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openStoredReview(item.id)}
                        disabled={historyBusyId === item.id}
                        className="rb-history-action"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteStoredReview(item.id)}
                        disabled={historyBusyId === item.id}
                        className="rb-history-action rb-history-action-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="rb-history-note">
              Reviews are kept for 90 days, then deleted. The full text of a document is
              only stored when you tick "Keep the full text with this review" before
              running it; without it, "Review a revision" still compares, but you paste
              the new version yourself. Quoted phrases are stored either way, because the
              review is built from them.
            </div>

            <div className="rb-feedback-actions">
              <button type="button" onClick={closeHistory} className="rb-feedback-submit">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
